// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

import React, { useState, useRef, useEffect } from "react";
import { sGetRaw, sSetRaw } from "../lib/scopedStorage";
import type { Theme } from "../lib/theme";
import type { Division, ItemState, Proposal, Subcategory, ShowToast } from "../types";
import type { Milestone, PrelimSchedule, SubSchedule } from "../lib/schedule";
import type { PhaseGroup } from "../lib/phaseGroups";
import { DEFAULT_PHASE_GROUPS, getSubPhaseGroup, hasPhaseOverride } from "../lib/phaseGroups";
import { walkItems, getItemTotal, isClarification } from "../lib/calculations";
import EmptyState from "../components/EmptyState";
import * as Icon from "../components/OEIcons";
import type { CostSchedule, ScheduleBand, ScheduleEntry } from "../lib/costSchedule";
import {
  migrateSubSchedule, toSubSchedule, resolveEntry, entrySpan,
  normalizeBands, splitBand, mergeAdjacentBands,
  buildCostLeaves, rollupSpan, hasOwnLeafSchedule, spreadLeaves,
} from "../lib/costSchedule";

const FONT     = "'Inter','Avenir Next','Century Gothic',sans-serif";
const LEFT_W   = 260;
const ROW_H    = 34;
const DIV_H    = 44;
const HEADER_H = 36;
/**
 * Extra header height for milestone names.
 *
 * A milestone's label sat in the same band as the month ticks and printed
 * straight through them -- "Milestone" over "Aug 26". Names get a band of
 * their own, and only when there are names to put in it.
 */
const MS_BAND  = 18;
const ACCENT   = "#4fd1c5";
/** How long a bar is when a drag first creates one, before it is stretched.
 *  A week reads as a real bar at every zoom without claiming a duration
 *  anyone chose. */
const NEW_BAR_DAYS = 7;

/**
 * Stacking inside the gantt, in one place because it only makes sense read
 * together.
 *
 * The frozen column has to beat everything that scrolls underneath it. It
 * used to sit at 5, below a bar's date popover at 30, so scrolling a schedule
 * sideways slid bars and their popovers out over the package names.
 */
const Z_BAR_POPOVER = 30;
const Z_LEFT_COL    = 36;
const Z_HEADER_ROW  = 40;
const Z_HEADER_LEFT = 41;
const Z_CASH_STRIP  = 38;   // over the sticky left column, under the sticky header
const Z_CROSSHAIR   = 30;   // over the bars, under the frozen left column

/**
 * A row tint that does not let the timeline show through it.
 *
 * The selected row replaced the frozen column's opaque fill with an 8%-alpha
 * tint, which is fine until something scrolls behind it -- then the bars read
 * straight through the package names. Layering the tint over the base keeps
 * the highlight and keeps the cell solid.
 */
const tintedOver = (base: string, tint?: string | null) =>
  tint ? `linear-gradient(${tint}, ${tint}), ${base}` : base;

const PX_PER_WEEK    = 56;
const PX_PER_MONTH   = 100;
const PX_PER_QUARTER = 220;

const LS_MILESTONES = "oe_milestones_v1";
const EMPTY_MILESTONES: Milestone[] = [];

type TimeScale  = "week" | "month" | "quarter";
type BarLabel   = "none" | "start" | "end" | "range";
type GroupMode  = "division" | "package" | "phase";

export type { Milestone };

// Division color palette (By Division mode)
const DIV_COLORS = [
  "#4fd1c5","#f6ad55","#b794f4","#68d391","#63b3ed",
  "#fc8181","#ecc94b","#f687b3","#4ade80","#60a5fa",
  "#fb923c","#a78bfa","#34d399","#f472b6","#38bdf8",
];

// ── date helpers ─────────────────────────────────────────────────────
function addDays(d: Date, n: number) { return new Date(d.getTime() + n * 86_400_000); }
function daysBetween(a: Date, b: Date) { return (b.getTime() - a.getTime()) / 86_400_000; }
function toISO(d: Date) { return d.toISOString().split("T")[0]; }
function fromISO(s: string) { return new Date(s + "T00:00:00"); }
/** Calendar months, so "18 months from Sep 13" lands on Mar 13, not on a
 *  4.333-week approximation of it. Clamps to month end (Jan 31 + 1 -> Feb 28). */
function addMonths(d: Date, n: number) {
  const r = new Date(d);
  const day = r.getDate();
  r.setDate(1); r.setMonth(r.getMonth() + n);
  const last = new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate();
  r.setDate(Math.min(day, last));
  return r;
}
type DurUnit = "days" | "weeks" | "months";
/** The estimate prices time in several spellings -- MO, Months, WK, Days. */
function timeUnitOf(unit: string | undefined): DurUnit | null {
  const u = (unit || "").trim().toLowerCase();
  if (/^(mo|mos|month|months)$/.test(u)) return "months";
  if (/^(wk|wks|week|weeks)$/.test(u)) return "weeks";
  if (/^(d|day|days)$/.test(u)) return "days";
  return null;
}
function endFromDuration(start: Date, n: number, unit: DurUnit) {
  return unit === "months" ? addMonths(start, n) : addDays(start, unit === "weeks" ? n * 7 : n);
}
/** Duration of a window in a unit, for display. Months are calendar months, rounded to a tenth. */
function durationIn(start: Date, end: Date, unit: DurUnit) {
  const days = daysBetween(start, end);
  if (unit === "days") return Math.round(days);
  if (unit === "weeks") return Math.round(days / 7 * 10) / 10;
  const m = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth())
    + (end.getDate() - start.getDate()) / 30.4;
  return Math.round(m * 10) / 10;
}
function fmtMonth(d: Date) { return d.toLocaleDateString("default", { month: "short", year: "2-digit" }); }
function fmtWeekLabel(d: Date) { return d.toLocaleDateString("default", { month: "short", day: "numeric" }); }
function fmtMonthYear(d: Date) { return d.toLocaleDateString("default", { month: "short", year: "numeric" }); }

// ── tick generators ──────────────────────────────────────────────────
type Tick = { label: string; pct: number };

function weekTicks(start: Date, end: Date): Tick[] {
  const total = Math.max(daysBetween(start, end), 1);
  const ticks: Tick[] = [];
  const d = new Date(start); d.setDate(d.getDate() + 7);
  while (d < end) {
    ticks.push({ label: fmtWeekLabel(new Date(d)), pct: daysBetween(start, d) / total * 100 });
    d.setDate(d.getDate() + 7);
  }
  return ticks;
}

function monthTicks(start: Date, end: Date): Tick[] {
  const total = Math.max(daysBetween(start, end), 1);
  const ticks: Tick[] = [];
  const d = new Date(start); d.setDate(1); d.setMonth(d.getMonth() + 1);
  while (d < end) {
    ticks.push({ label: fmtMonth(new Date(d)), pct: daysBetween(start, d) / total * 100 });
    d.setMonth(d.getMonth() + 1);
  }
  return ticks;
}

function quarterTicks(start: Date, end: Date): Tick[] {
  const total = Math.max(daysBetween(start, end), 1);
  const ticks: Tick[] = [];
  const d = new Date(start); d.setDate(1);
  d.setMonth(d.getMonth() + (3 - (d.getMonth() % 3)));
  while (d < end) {
    const q = Math.floor(d.getMonth() / 3) + 1;
    ticks.push({ label: `Q${q} '${String(d.getFullYear()).slice(2)}`, pct: daysBetween(start, d) / total * 100 });
    d.setMonth(d.getMonth() + 3);
  }
  return ticks;
}

function getTicks(scale: TimeScale, start: Date, end: Date): Tick[] {
  return scale === "week" ? weekTicks(start, end)
    : scale === "quarter" ? quarterTicks(start, end)
    : monthTicks(start, end);
}

function ganttMinWidth(scale: TimeScale, start: Date, end: Date): number {
  const days = daysBetween(start, end);
  return Math.max(
    scale === "week"    ? (days / 7)   * PX_PER_WEEK
    : scale === "quarter" ? (days / 91.3) * PX_PER_QUARTER
    : (days / 30.44)  * PX_PER_MONTH,
    400,
  );
}

// ── Auto-distribute across the user's Phases ──────────────────
// Sequence and timing come from the Phases the user edits in Settings, not
// from a private table. There used to be a second, hard-coded division ->
// phase map here that disagreed with those Phases (Div 32 was "Site Work" to
// the user and "Finishes" to this function), so reordering or re-grouping a
// phase changed the colours and the grouping but not the dates.
//
// Phases are laid out in `order`, each window spanning three slots so
// neighbours overlap the way trades actually do -- MEP rough-in starts before
// the structure is finished.
function phaseWindows(groups: PhaseGroup[]): Record<string, { start: number; end: number }> {
  const ordered = [...groups].sort((a, b) => a.order - b.order);
  const n = Math.max(ordered.length, 1);
  const step = 1 / (n + 2);
  const out: Record<string, { start: number; end: number }> = {};
  ordered.forEach((g, i) => {
    out[g.id] = { start: i * step, end: Math.min(1, (i + 3) * step) };
  });
  // Divisions in no phase land at the end, alongside the last one.
  out["__other__"] = out[ordered[n - 1]?.id] ?? { start: 0.7, end: 1 };
  return out;
}

function smartAutoDistribute(
  divisions: Division[],
  subTotals: Record<string, number>,
  projectStart: Date,
  projectEnd: Date,
  groups: PhaseGroup[],
): SubSchedule {
  const totalDays = Math.max(daysBetween(projectStart, projectEnd), 1);
  const windows = phaseWindows(groups);
  const slots = new Map<string, Array<{ id: string; cost: number }>>();

  divisions.forEach(div => {
    div.subcategories.forEach(sub => {
      const cost = subTotals[sub.id] || 0;
      if (cost <= 0) return;
      // Resolved per subcategory, not per division. A Div 06 trim
      // subcategory pinned to Finishes has to be PLACED in the Finishes
      // window -- otherwise every re-run drags it back to Structure and
      // undoes the hand placement it needed in the first place.
      const key = getSubPhaseGroup(div.id, sub, groups)?.id ?? "__other__";
      if (!slots.has(key)) slots.set(key, []);
      slots.get(key)!.push({ id: sub.id, cost });
    });
  });

  const result: SubSchedule = {};

  slots.forEach((subs, key) => {
    const win = windows[key] ?? windows["__other__"];
    const winStart = win.start * totalDays;
    const winEnd   = win.end   * totalDays;
    const winDur   = winEnd - winStart;
    const totalCost = subs.reduce((s, x) => s + x.cost, 0) || 1;
    // Slight stagger between subs in the same phase (4% of window per sub)
    const stagger = winDur * 0.04;

    subs.forEach(({ id, cost }, i) => {
      const frac     = cost / totalCost;
      const startDay = Math.round(winStart + i * stagger);
      const dur      = Math.max(frac * winDur * 1.5, 14);
      const endDay   = Math.round(Math.min(startDay + dur, winEnd));
      result[id] = {
        startDate: toISO(addDays(projectStart, startDay)),
        endDate:   toISO(addDays(projectStart, Math.max(endDay, startDay + 14))),
        pctComplete: 0,
      };
    });
  });

  return result;
}

// ── dominant proposal for a subcategory (by cost) ────────────────────
function dominantProposalId(sub: Subcategory, itemState: Record<string, ItemState>): string {
  const costByProp: Record<string, number> = {};
  sub.rows.forEach(row => {
    if (row.type !== "item") return;
    const it = itemState[row.id];
    if (!it) return;
    const prop = it.proposal || "__unassigned__";
    costByProp[prop] = (costByProp[prop] || 0) + (it.qty || 0) * (it.unitCost || 0);
  });
  let best = "__unassigned__", bestCost = -1;
  for (const [pid, cost] of Object.entries(costByProp)) {
    if (cost > bestCost) { best = pid; bestCost = cost; }
  }
  return best;
}

type PackageGroup = {
  id: string; name: string; colorHex: string;
  subs: Array<{ div: Division; sub: Subcategory; cost: number }>;
  totalCost: number; spanStart: Date | null; spanEnd: Date | null;
};

// ── ScheduleTabProps ──────────────────────────────────────────────────
interface ScheduleTabProps {
  T: Theme; isMobile: boolean; isTablet: boolean;
  divisions: Division[]; itemState: Record<string, ItemState>;
  proposals: Proposal[];
  phaseGroups?: PhaseGroup[];
  subTotals: Record<string, number>;
  final: { subtotal: number; overhead: number; profit: number; contingency: number; total: number };
  fmt: (n: number) => string;
  schedule: PrelimSchedule; setSchedule: React.Dispatch<React.SetStateAction<PrelimSchedule>>;
  /** Project duration in months -- THE duration. Same field Project Info
   *  edits and every PDF prints. The schedule derives its span from it. */
  duration: number; setDuration: React.Dispatch<React.SetStateAction<number>>;
  costSchedule: CostSchedule; setCostSchedule: React.Dispatch<React.SetStateAction<CostSchedule>>;
  setActiveTab: (t: string) => void;
  /** Open the full cash flow. Absent when the plan does not include it. */
  onOpenCashflow?: () => void;
  /** Pin a subcategory to a phase, or clear it back to following its
   *  division. Absent in read-only contexts; the chip hides itself. */
  setSubPhase?: (divId: string, subId: string, phaseId?: string) => void;
  showToast: ShowToast;
  uid: () => string;
  /** Push an undo point before mutating schedule dates. */
  scheduleSnapshot: () => void;
}

export default function ScheduleTab({
  T, isMobile, isTablet,
  divisions, itemState, proposals,
  phaseGroups = DEFAULT_PHASE_GROUPS,
  subTotals, fmt,
  schedule, setSchedule, duration, setDuration,
  scheduleSnapshot,
  costSchedule, setCostSchedule,
  showToast, uid,
  setActiveTab, onOpenCashflow, setSubPhase,
}: ScheduleTabProps) {

  // ── project date range ─────────────────────────────────────────────
  const projectStart = schedule.projectStartDate ? fromISO(schedule.projectStartDate) : null;
  // The project had two durations: this one (Project Info, saved to the
  // cloud, printed on every PDF) and schedule.totalDuration (this toolbar,
  // nowhere else). They drifted -- a 12-month schedule under an 18-month
  // estimate. There is one now; the schedule reads it and edits it in place.
  const durWeeks = Math.round(Math.max(1, duration || 12) * 4.333);
  const projectEnd = projectStart ? addDays(projectStart, durWeeks * 7) : null;
  const totalDays  = projectStart && projectEnd ? Math.max(daysBetween(projectStart, projectEnd), 1) : 1;

  // ── ui state ───────────────────────────────────────────────────────
  // Presentation is a set of switches over one view, not a second mode.
  // Edit/Present swapped six toolbar controls and the entire row structure at
  // once, so you lost your place, and "Present" was fixed at group level with
  // no way to show more. These say what they do and compose.
  type Detail = "groups" | "subs" | "items";
  const [detail,          setDetail]          = useState<Detail>("subs");
  const [showCost,        setShowCost]        = useState(true);
  /** Week index under the pointer on the cash strip, or null when it is not over it. */
  const [cashHover,       setCashHover]       = useState<number | null>(null);
  const [mono,            setMono]            = useState(false);
  const [showTitleBlock,  setShowTitleBlock]  = useState(false);
  const [showTools,       setShowTools]       = useState(true);
  const [displayOpen,     setDisplayOpen]     = useState(false);
  // Unit for the duration field in the bar popover. Sticky across bars so a
  // user working in months is not reset to weeks on every click.
  const [durUnit,         setDurUnit]         = useState<DurUnit>("weeks");
  const [timeScale,       setTimeScale]       = useState<TimeScale>("month");
  const [barLabel,        setBarLabel]        = useState<BarLabel>("none");
  const [showConfidence,  setShowConfidence]  = useState(false);
  // ★ Default to Package grouping — the owner-facing view
  const [groupMode,   setGroupMode]       = useState<GroupMode>("package");
  const [expandedDivs, setExpandedDivs]   = useState<Set<string>>(() => {
    const s = new Set<string>(); divisions.forEach(d => s.add(d.id)); return s;
  });
  const [expandedPkgs, setExpandedPkgs]   = useState<Set<string>>(() =>
    new Set(proposals.map(p => p.id).concat(["__unassigned__"]))
  );
  const [selectedSubId, setSelectedSubId] = useState<string | null>(null);
  // Which subcategories are opened down to their line items. Closed by
  // default: most scope is scheduled at subcategory level and a row per item
  // would bury that.
  const [expandedSubsSched, setExpandedSubsSched] = useState<Set<string>>(new Set());
  // Milestones live on the schedule, which is per-project, snapshotted, and
  // already covered by the undo stack. Every write takes an undo point unless
  // the caller opts out -- typing in the label field snapshots once on focus
  // rather than once per keystroke, which would flush the 40-deep stack.
  const milestones: Milestone[] = schedule.milestones ?? EMPTY_MILESTONES;
  const setMilestones = (
    next: Milestone[] | ((prev: Milestone[]) => Milestone[]),
    opts?: { snapshot?: boolean },
  ) => {
    if (opts?.snapshot !== false) scheduleSnapshot();
    setSchedule(p => ({
      ...p,
      milestones: typeof next === "function" ? next(p.milestones ?? []) : next,
    }));
  };
  /** The preview marker that follows the pointer while placing a milestone. */
  const msGhostRef = useRef<HTMLDivElement | null>(null);
  /** The vertical guide under the pointer, and the date it is over. */
  const crosshairRef = useRef<HTMLDivElement | null>(null);
  const crosshairDateRef = useRef<HTMLDivElement | null>(null);

  const [selectedMs, setSelectedMs]       = useState<string | null>(null);  // selected milestone id
  const [milestoneMode, setMilestoneMode] = useState(false);               // click-to-place mode

  /**
   * Escape leaves milestone mode.
   *
   * It was a mode with no way out but placing one: having turned it on by
   * mistake, the only exit was to drop a marker somewhere and delete it.
   */
  useEffect(() => {
    if (!milestoneMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const tag = (document.activeElement as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      setMilestoneMode(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [milestoneMode]);

  const ganttRef = useRef<HTMLDivElement>(null);

  // Adopt the old global list once per project, so existing users keep the
  // milestones they can currently see; from here the two diverge. The legacy
  // key is left in place because other projects have not adopted yet.
  const msAdopted = useRef(false);
  useEffect(() => {
    if (msAdopted.current || schedule.milestones !== undefined) return;
    msAdopted.current = true;
    let legacy: Milestone[] = [];
    try { legacy = JSON.parse(sGetRaw(LS_MILESTONES) || "[]"); } catch { legacy = []; }
    setSchedule(p => (p.milestones === undefined ? { ...p, milestones: legacy } : p));
  }, [schedule.milestones, setSchedule]);

  // Close milestone popover on outside click
  useEffect(() => {
    if (!selectedMs) return;
    const handler = () => setSelectedMs(null);
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [selectedMs]);

  // Detail sets a baseline for the expansion sets the rows already use, rather
  // than introducing a second way to decide what is visible. Chevrons still
  // override it row by row.
  const allSubIds = React.useMemo(
    () => divisions.flatMap(d => d.subcategories.map(sub => sub.id)),
    [divisions]);
  useEffect(() => {
    if (detail === "groups") {
      setExpandedDivs(new Set()); setExpandedPkgs(new Set()); setExpandedSubsSched(new Set());
    } else if (detail === "subs") {
      setExpandedDivs(new Set(divisions.map(d => d.id)));
      setExpandedPkgs(new Set(proposals.map(pr => pr.id).concat(["__unassigned__"])));
      setExpandedSubsSched(new Set());
    } else {
      setExpandedDivs(new Set(divisions.map(d => d.id)));
      setExpandedPkgs(new Set(proposals.map(pr => pr.id).concat(["__unassigned__"])));
      setExpandedSubsSched(new Set(allSubIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  /** Monochrome for printing: one neutral instead of the colour coding. */
  const shade = React.useCallback((c: string) => (mono ? "#8b93a1" : c), [mono]);

  // ── drag state ─────────────────────────────────────────────────────
  // Every row reads its schedule through the resolver, so a subcategory with
  // no schedule of its own still draws its division's bars (faintly) and a
  // line item still draws its subcategory's. `subSchedule` is the same data
  // collapsed to one span per node, for the rollups that only need extent.
  const subSchedule = React.useMemo(() => toSubSchedule(costSchedule), [costSchedule]);

  // Bars are drawn bottom-up. A subcategory's extent is the union of what its
  // line items actually resolve to, so an item dragged outside its parent's
  // window widens the parent instead of silently contradicting it; a division
  // is the union of its subcategories in turn. With nothing scheduled below,
  // the union is exactly the node's own span and nothing looks different.
  const leavesBySub = React.useMemo(() => buildCostLeaves(divisions, itemState), [divisions, itemState]);

  /**
   * The cash curve this schedule produces, on this schedule's own timeline.
   *
   * Cash flow is already derived from these bands, but it lives on another
   * tab — so dragging a band changed the curve and nobody ever saw it
   * happen. The link was real and invisible, which is the same as not being
   * trusted. Drawn here against the same x-axis as the bars above it, so a
   * band at month seven sits over the hump at month seven, and moving the
   * band moves the hump while you are still holding it.
   *
   * Weekly, because that is what spreadLeaves returns and what the bars are
   * measured in; the figures quoted are monthly, because that is how
   * anybody talks about a draw.
   */
  const cashWeeks = React.useMemo(() => {
    if (!projectStart || !projectEnd) return [] as number[];
    const weeks = Math.max(1, Math.ceil(daysBetween(projectStart, projectEnd) / 7));
    const all = Object.values(leavesBySub).flat();
    return spreadLeaves(all, costSchedule, projectStart, weeks);
  }, [leavesBySub, costSchedule, projectStart, projectEnd]);

  const cashPeak = React.useMemo(() => {
    // The busiest month, as four consecutive weeks — the number somebody
    // actually has to finance.
    let best = { at: 0, amt: 0 };
    for (let i = 0; i < cashWeeks.length; i++) {
      const amt = cashWeeks.slice(i, i + 4).reduce((a, v) => a + v, 0);
      if (amt > best.amt) best = { at: i, amt };
    }
    return best;
  }, [cashWeeks]);
  const subRollup = React.useCallback(
    (subId: string) => rollupSpan(leavesBySub[subId] || [], costSchedule),
    [leavesBySub, costSchedule]);

  /** The bands actually governing a node, and whether they are its own. */
  const effectiveBands = (nodeId: string, parentIds: string[]) => {
    const own = costSchedule[nodeId];
    if (own?.bands?.length) return { bands: normalizeBands(own.bands), own: true };
    const inherited = resolveEntry(parentIds, costSchedule);
    return { bands: inherited ? normalizeBands(inherited.bands) : [], own: false };
  };

  const writeBands = (nodeId: string, bands: ScheduleBand[]) =>
    setCostSchedule(prev => {
      const next = { ...prev };
      const clean = normalizeBands(bands);
      if (clean.length === 0) delete next[nodeId];
      else next[nodeId] = { ...(prev[nodeId] || {}), bands: clean };
      return next;
    });

  /** Pointer capture keeps a drag alive when the pointer leaves the element.
   *  It is an improvement to the gesture, not a precondition for it, so a
   *  browser that refuses must not take the whole drag down with it. */
  const tryCapture = (el: HTMLElement, pointerId: number) => {
    try { el.setPointerCapture(pointerId); } catch { /* drag still works */ }
  };

  /**
   * A drag started on an unscheduled row, before it has become a bar.
   *
   * Nothing is written until the pointer actually travels: a bar that appears
   * wherever you happen to click is a bar you then have to find and delete.
   */
  const pendingCreateRef = useRef<{
    nodeId: string; startX: number; startDate: Date;
  } | null>(null);

  const dragRef = useRef<{
    subId: string; bandIdx: number; type: "move" | "resize";
    startX: number; origStart: Date; origEnd: Date; origBands: ScheduleBand[];
    ganttWidth: number;
  } | null>(null);

  // ── division metadata ──────────────────────────────────────────────
  type DivMeta = { tot: number; spanStart: Date | null; spanEnd: Date | null; colorIdx: number };
  const divMeta: Record<string, DivMeta> = {};
  let colorIdx = 0;
  divisions.forEach(div => {
    const subs = div.subcategories.filter(s => (subTotals[s.id] || 0) > 0);
    const tot  = subs.reduce((s, x) => s + (subTotals[x.id] || 0), 0);
    let spanStart: Date | null = null, spanEnd: Date | null = null;
    subs.forEach(sub => {
      const e = subRollup(sub.id);
      if (e) {
        const s2 = fromISO(e.start), e2 = fromISO(e.end);
        if (!spanStart || s2 < spanStart) spanStart = s2;
        if (!spanEnd   || e2 > spanEnd)   spanEnd   = e2;
      }
    });
    divMeta[div.id] = { tot, spanStart, spanEnd, colorIdx: tot > 0 ? colorIdx++ : -1 };
  });

  // ── package groups ─────────────────────────────────────────────────
  const packageGroups: PackageGroup[] = React.useMemo(() => {
    const map = new Map<string, PackageGroup>();
    proposals.forEach(p => {
      map.set(p.id, {
        id: p.id, name: p.name,
        colorHex: T.proposalColors[p.color % T.proposalColors.length]?.bar || ACCENT,
        subs: [], totalCost: 0, spanStart: null, spanEnd: null,
      });
    });
    divisions.forEach(div => {
      div.subcategories.forEach(sub => {
        const cost = subTotals[sub.id] || 0;
        if (cost === 0) return;
        const pid = dominantProposalId(sub, itemState);
        let grp = map.get(pid);
        if (!grp) {
          if (!map.has("__unassigned__")) {
            map.set("__unassigned__", {
              id: "__unassigned__", name: "Unassigned",
              colorHex: T.borderSecondary,
              subs: [], totalCost: 0, spanStart: null, spanEnd: null,
            });
          }
          grp = map.get("__unassigned__")!;
        }
        grp.subs.push({ div, sub, cost });
        grp.totalCost += cost;
        const entry = subRollup(sub.id);
        if (entry) {
          const s = fromISO(entry.start), e = fromISO(entry.end);
          if (!grp.spanStart || s < grp.spanStart) grp.spanStart = s;
          if (!grp.spanEnd   || e > grp.spanEnd)   grp.spanEnd   = e;
        }
      });
    });
    return Array.from(map.values()).filter(g => g.subs.length > 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals, divisions, subTotals, itemState, subSchedule, T]);

  // ── per-subcategory cost confidence (0 = certain, 1 = uncertain) ───
  // Uncertainty = fraction of cost that is NOT Hard Bid
  const subUncertainty = React.useMemo(() => {
    const result: Record<string, number> = {};
    divisions.forEach(div => {
      div.subcategories.forEach(sub => {
        let hardBid = 0, total = 0;
        sub.rows.forEach(row => {
          if (row.type !== "item") return;
          const it = itemState[row.id];
          if (!it) return;
          const cost = (it.qty || 0) * (it.unitCost || 0);
          total += cost;
          if (it.costStatus === "Hard Bid") hardBid += cost;
        });
        result[sub.id] = total > 0 ? 1 - hardBid / total : 0;
      });
    });
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisions, itemState]);

  // ── per-subcategory dominant proposal color ────────────────────────
  const subPkgColor = React.useMemo(() => {
    const result: Record<string, string> = {};
    divisions.forEach(div => {
      div.subcategories.forEach(sub => {
        const pid = dominantProposalId(sub, itemState);
        const prop = proposals.find(p => p.id === pid);
        result[sub.id] = prop
          ? T.proposalColors[prop.color % T.proposalColors.length]?.bar || ACCENT
          : T.borderSecondary;
      });
    });
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisions, itemState, proposals, T]);

  // ── phase data ───────────────────────────────────────────────
  // Groups active divisions by their PhaseGroup, in construction sequence order.
  // Each group gets: name, color, all subs, totalCost, spanStart/End.
  type PhaseGroupRow = {
    id: string; name: string; colorHex: string;
    subs: Array<{ div: Division; sub: Subcategory; cost: number }>;
    totalCost: number; spanStart: Date | null; spanEnd: Date | null;
  };
  const phaseGroupRows: PhaseGroupRow[] = React.useMemo(() => {
    const activeDivsHere = divisions.filter(d => (divMeta[d.id]?.tot || 0) > 0);
    // Buckets hold SUBCATEGORIES, not divisions. Grouping divisions could
    // only ever put all of Div 06 in one phase, which is the whole problem:
    // its rough framing is Structure and its casework is Finishes. A division
    // can now appear under two phase rows, which is correct -- its rollup bar
    // spans both.
    const buckets = new Map<string, PhaseGroupRow["subs"]>();
    activeDivsHere.forEach(div => {
      div.subcategories.forEach(sub => {
        const cost = subTotals[sub.id] || 0;
        if (cost === 0) return;
        const key = getSubPhaseGroup(div.id, sub, phaseGroups)?.id ?? "__other__";
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key)!.push({ div, sub, cost });
      });
    });
    const rows: PhaseGroupRow[] = [];
    const emit = (id: string, name: string, colorHex: string) => {
      const subs = buckets.get(id);
      if (!subs || subs.length === 0) return;
      let totalCost = 0;
      let spanStart: Date | null = null, spanEnd: Date | null = null;
      subs.forEach(({ sub, cost }) => {
        totalCost += cost;
        const entry = subRollup(sub.id);
        if (entry) {
          const s = fromISO(entry.start), e = fromISO(entry.end);
          if (!spanStart || s < spanStart) spanStart = s;
          if (!spanEnd   || e > spanEnd)   spanEnd   = e;
        }
      });
      rows.push({ id, name, colorHex, subs, totalCost, spanStart, spanEnd });
    };
    [...phaseGroups].sort((a, b) => a.order - b.order).forEach(g => emit(g.id, g.name, g.color));
    emit("__other__", "Other", "#6b7280");
    return rows;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisions, subTotals, subSchedule, phaseGroups, divMeta]);

  // Per-sub phase color (used in division mode to color-stripe by phase)
  const subPhaseColor = React.useMemo(() => {
    const result: Record<string, string> = {};
    divisions.forEach(div => {
      div.subcategories.forEach(sub => {
        result[sub.id] = getSubPhaseGroup(div.id, sub, phaseGroups)?.color ?? ACCENT;
      });
    });
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisions, phaseGroups]);

  // ── phase chip on a subcategory row ────────────────────────────────
  // A division belongs to exactly one phase; its work does not. This pins one
  // subcategory elsewhere, which changes its colour, its grouping in Schedule
  // and Cash Flow, and where Auto-Distribute places it.
  const [phaseMenu, setPhaseMenu] = useState<{ divId: string; sub: Subcategory; x: number; y: number } | null>(null);
  const phaseChip = (divId: string, sub: Subcategory) => {
    if (!setSubPhase) return null;
    const pg = getSubPhaseGroup(divId, sub, phaseGroups);
    const pinned = hasPhaseOverride(divId, sub, phaseGroups);
    return (
      <div style={{ flexShrink: 0, marginRight: 8, lineHeight: 0 }}>
        <button
          onClick={e => {
            e.stopPropagation();
            const r = e.currentTarget.getBoundingClientRect();
            setPhaseMenu(p => (p && p.sub.id === sub.id ? null : { divId, sub, x: r.right, y: r.bottom + 4 }));
          }}
          title={pg
            ? `Phase: ${pg.name}${pinned ? " (pinned to this subcategory)" : " (follows the division)"} \u2014 click to change`
            : "No phase \u2014 click to set one"}
          style={{ width: 11, height: 11, borderRadius: 999, padding: 0, cursor: "pointer", display: "block",
            background: pg?.color ?? "transparent",
            border: pinned ? `2px solid ${T.textPrimary}` : `1px solid ${T.borderSecondary}`,
            boxSizing: "content-box" }} />
      </div>
    );
  };

  // ── bar position helpers ───────────────────────────────────────────
  const barL = (d: string) => !projectStart ? 0 :
    Math.max(0, Math.min(100, daysBetween(projectStart, fromISO(d)) / totalDays * 100));
  const barW = (s: string, e: string) =>
    Math.max(0.4, Math.min(100, daysBetween(fromISO(s), fromISO(e)) / totalDays * 100));

  // ── bar label text ─────────────────────────────────────────────────
  const getBarLabelText = (startDate: string, endDate: string): string => {
    if (barLabel === "none")  return "";
    if (barLabel === "start") return fmtWeekLabel(fromISO(startDate));
    if (barLabel === "end")   return fmtWeekLabel(fromISO(endDate));
    return `${fmtWeekLabel(fromISO(startDate))} – ${fmtWeekLabel(fromISO(endDate))}`;
  };

  // ── drag handlers ──────────────────────────────────────────────────
  // Dragging a bar a row has inherited is taken as intent to schedule that row
  // separately: the parent's bands are copied down first, then the drag applies
  // to the copy. No separate "detach" step to discover.
  const onBarDown = (
    ev: React.PointerEvent, nodeId: string, parentIds: string[],
    bandIdx: number, type: "move" | "resize",
  ) => {
    ev.stopPropagation();
    if (!projectStart || !projectEnd || !ganttRef.current) return;
    const { bands, own } = effectiveBands(nodeId, parentIds);
    const band = bands[bandIdx];
    if (!band) return;
    tryCapture(ev.currentTarget as HTMLElement, ev.pointerId);
    scheduleSnapshot();
    if (!own) writeBands(nodeId, bands);
    dragRef.current = {
      subId: nodeId, bandIdx, type, startX: ev.clientX,
      origBands: bands,
      origStart: fromISO(band.start),
      origEnd:   fromISO(band.end),
      ganttWidth: ganttRef.current.scrollWidth - LEFT_W,
    };
    setSelectedSubId(nodeId);
  };

  /** How wide the bar a drag starts out as, in track percent. */
  const ghostWidthPct = Math.max(0.4, Math.min(100, (NEW_BAR_DAYS / totalDays) * 100));

  /**
   * Slides the hover preview to sit under the pointer.
   *
   * Written straight to the node's style rather than through state: this fires
   * for every pointer move across every unscheduled row, and re-rendering the
   * whole gantt at pointer rate to move one translucent rectangle would make
   * the schedule feel heavy for no gain.
   */
  const moveGhost = (ev: React.PointerEvent) => {
    const host = ev.currentTarget as HTMLElement;
    const ghost = host.firstElementChild as HTMLElement | null;
    if (!ghost) return;
    const rect = host.getBoundingClientRect();
    if (rect.width <= 0) return;
    const frac = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
    // Held inside the row so the preview never promises a bar that would be
    // clamped somewhere else on release.
    ghost.style.left = `${Math.min(frac * 100, Math.max(0, 100 - ghostWidthPct))}%`;
    ghost.style.opacity = "1";
  };

  /** Takes the preview away the moment the pointer is no longer on the row. */
  const hideGhost = (ev: React.PointerEvent) => {
    const ghost = (ev.currentTarget as HTMLElement).firstElementChild as HTMLElement | null;
    if (ghost) ghost.style.opacity = "0";
  };

  /**
   * Clears every preview still showing.
   *
   * A pointer that is captured, or that leaves the window, does not
   * reliably give the row a leave event, and a ghost left behind on a row
   * nobody is pointing at reads as a bar that is really scheduled.
   */
  const clearGhosts = () => {
    ganttRef.current?.querySelectorAll<HTMLElement>(".gantt-ghost")
      .forEach(g => { g.style.opacity = "0"; });
    hideMilestoneGhost();
  };

  /**
   * Drag across an unscheduled row to give it a bar.
   *
   * Auto-Distribute used to be the only way anything got onto the schedule:
   * every other gesture here edits a bar that already exists, so a estimator
   * who did not want the whole estimate laid out at once had no way in at
   * all, just rows saying "unscheduled" and no affordance.
   */
  const onEmptyDown = (ev: React.PointerEvent, nodeId: string) => {
    if (ev.button !== 0 || milestoneMode) return;
    if (!projectStart || !projectEnd || !ganttRef.current) return;
    ev.stopPropagation();
    const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    const frac = rect.width > 0 ? (ev.clientX - rect.left) / rect.width : 0;
    const startDate = addDays(projectStart, Math.round(Math.max(0, Math.min(1, frac)) * totalDays));
    // Recorded before capture is attempted: losing the capture must not lose
    // the gesture.
    pendingCreateRef.current = { nodeId, startX: ev.clientX, startDate };
    tryCapture(ev.currentTarget as HTMLElement, ev.pointerId);
    setSelectedSubId(nodeId);
  };

  /** Turns a travelled drag into the row's first bar, then hands the rest of
   *  the gesture to the ordinary resize path so it stretches as you pull. */
  const materialisePending = (clientX: number) => {
    const pend = pendingCreateRef.current;
    if (!pend || !projectStart || !projectEnd || !ganttRef.current) return false;
    if (Math.abs(clientX - pend.startX) < 4) return false;
    pendingCreateRef.current = null;

    const MIN_DAYS = NEW_BAR_DAYS;
    let start = pend.startDate;
    if (addDays(start, MIN_DAYS) > projectEnd) start = addDays(projectEnd, -MIN_DAYS);
    if (start < projectStart) start = new Date(projectStart);
    const end = addDays(start, MIN_DAYS);

    scheduleSnapshot();
    const bands: ScheduleBand[] = [{ start: toISO(start), end: toISO(end), pct: 100 }];
    writeBands(pend.nodeId, bands);
    dragRef.current = {
      subId: pend.nodeId, bandIdx: 0, type: "resize", startX: pend.startX,
      origBands: bands, origStart: start, origEnd: end,
      ganttWidth: ganttRef.current.scrollWidth - LEFT_W,
    };
    return true;
  };

  const onMove = (ev: React.PointerEvent) => {
    if (pendingCreateRef.current && !materialisePending(ev.clientX)) return;
    const dr = dragRef.current;
    if (!dr || !projectStart || !projectEnd) return;
    const delta = (ev.clientX - dr.startX) / dr.ganttWidth * totalDays;
    let start = dr.origStart, end = dr.origEnd;
    if (dr.type === "move") {
      const dur = daysBetween(dr.origStart, dr.origEnd);
      start = addDays(dr.origStart, delta); end = addDays(start, dur);
      if (start < projectStart) { start = new Date(projectStart); end = addDays(start, dur); }
      if (end > projectEnd)     { end = new Date(projectEnd);     start = addDays(end, -dur); }
    } else {
      end = addDays(dr.origEnd, delta);
      if (end < addDays(dr.origStart, 7)) end = addDays(dr.origStart, 7);
      if (end > projectEnd) end = new Date(projectEnd);
    }
    const next = dr.origBands.map((b: ScheduleBand, i: number) =>
      i === dr.bandIdx ? { ...b, start: toISO(start), end: toISO(end) } : b);
    writeBands(dr.subId, next);
  };
  // Bands dragged back over each other merge on release, so a split can be
  // undone with the same gesture that made it.
  const onUp = () => {
    // A press that never travelled is not a bar: let it go without writing.
    pendingCreateRef.current = null;
    clearGhosts();
    const dr = dragRef.current;
    dragRef.current = null;
    if (!dr) return;
    const own = costSchedule[dr.subId];
    if (own?.bands && own.bands.length > 1) writeBands(dr.subId, mergeAdjacentBands(own.bands));
  };

  /**
   * A release anywhere ends the gesture.
   *
   * The container's own pointerup is not enough: a drag that creates a bar
   * replaces the row it started on, so the release can land on a node that is
   * no longer in the tree, and a release outside the gantt never reaches it at
   * all. Either way the drag stayed live and the hover preview stayed lit on
   * a row nobody was pointing at, which reads as a bar that is really there.
   */
  const onUpRef = useRef(onUp);
  onUpRef.current = onUp;
  useEffect(() => {
    const release = () => onUpRef.current();
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
    return () => {
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
    };
  }, []);

  /** Cut a band in two at a point along it, keeping both on the same row. */
  const splitAt = (nodeId: string, parentIds: string[], bandIdx: number, atISO: string) => {
    const { bands, own } = effectiveBands(nodeId, parentIds);
    const target = bands[bandIdx];
    if (!target) return;
    const halves = splitBand(target, atISO);
    if (!halves) { showToast("Bar is too short to split"); return; }
    scheduleSnapshot();
    if (!own) writeBands(nodeId, bands);
    writeBands(nodeId, bands.flatMap((b, i) => (i === bandIdx ? halves : [b])));
  };

  const updateDate = (
    nodeId: string, parentIds: string[], bandIdx: number,
    field: "start" | "end", val: string,
  ) => {
    if (!val) return;
    const { bands } = effectiveBands(nodeId, parentIds);
    if (!bands[bandIdx]) return;
    scheduleSnapshot();
    writeBands(nodeId, bands.map((b, i) => {
      if (i !== bandIdx) return b;
      const up = { ...b, [field]: val };
      if (field === "start" && up.end <= val) up.end = toISO(addDays(fromISO(val), 7));
      if (field === "end" && up.start >= val) up.start = toISO(addDays(fromISO(val), -7));
      return up;
    }));
  };

  /** Set a band's end as start + duration. The user says "18 months"; the
   *  app does the calendar arithmetic. */
  const setBandDuration = (
    nodeId: string, parentIds: string[], bandIdx: number, n: number, unit: DurUnit,
  ) => {
    if (!Number.isFinite(n) || n <= 0) return;
    const { bands } = effectiveBands(nodeId, parentIds);
    const b = bands[bandIdx];
    if (!b) return;
    scheduleSnapshot();
    let end = endFromDuration(fromISO(b.start), n, unit);
    if (projectEnd && end > projectEnd) {
      // The bar cannot run past the Gantt, but the user asked for a number
      // and is about to see a different one. Say what happened and where the
      // real disagreement is: the project is shorter than this scope.
      const got = durationIn(fromISO(b.start), projectEnd, unit);
      const unitLbl = unit === "months" ? "mo" : unit === "weeks" ? "wks" : "days";
      showToast(`${n} ${unitLbl} runs past the project end (${fmtWeekLabel(projectEnd)}). Set to ${got} ${unitLbl}. Extend DUR in the toolbar if the project really is that long.`);
      end = new Date(projectEnd);
    }
    writeBands(nodeId, bands.map((x, i) => (i === bandIdx ? { ...x, end: toISO(end) } : x)));
  };

  const handleAutoDistribute = () => {
    if (!projectStart || !projectEnd) { showToast("Set a project start date first"); return; }
    // Auto-Distribute replaces the whole schedule. Bars moved by hand come
    // back with a re-run; line items scheduled separately and split bars do
    // not. Say exactly what is about to go before it goes. Silent the first
    // time, when there is nothing to lose.
    const existing = Object.values(costSchedule).filter(e => e?.bands?.length);
    if (existing.length > 0) {
      const splits = existing.filter(e => e.bands.length > 1).length;
      const parts = [`This repositions every bar (${existing.length} scheduled).`];
      if (itemLevelCount > 0) parts.push(`${itemLevelCount} line item${itemLevelCount === 1 ? "" : "s"} scheduled separately will be discarded.`);
      if (splits > 0) parts.push(`${splits} split bar${splits === 1 ? "" : "s"} will be merged back to one.`);
      parts.push("Ctrl+Z undoes it. Continue?");
      if (!window.confirm(parts.join("\n"))) return;
    }
    scheduleSnapshot();
    setCostSchedule(migrateSubSchedule(smartAutoDistribute(divisions, subTotals, projectStart, projectEnd, phaseGroups)));
    // The old message named a CSI sequence, which came from a hard-coded
    // division -> phase table that no longer exists. Sequence comes from the
    // user's Phases now, and bar length comes from cost -- say that, and point
    // at the lever when there are no Phases to sequence by.
    showToast(phaseGroups.length > 0
      ? `Distributed across ${phaseGroups.length} phase${phaseGroups.length === 1 ? "" : "s"} — bar lengths scale with cost`
      : "Distributed — bar lengths scale with cost. Add Phases in Settings to control the sequence.");
  };

  /**
   * Where along the timeline a pointer is, as a fraction of the project.
   *
   * The one place this arithmetic lives, so the preview cannot land
   * somewhere the click would not: both the ghost and the milestone it
   * becomes are placed from the same number.
   */
  const timelineFrac = (clientX: number): number | null => {
    const el = ganttRef.current;
    if (!el) return null;
    const trackW = el.scrollWidth - LEFT_W;
    if (trackW <= 0) return null;
    const relX = (clientX - el.getBoundingClientRect().left) + el.scrollLeft - LEFT_W;
    return Math.max(0, Math.min(1, relX / trackW));
  };

  /**
   * Slides the milestone preview under the pointer.
   *
   * Written straight to the node, like the bar ghosts and for the same
   * reason: this fires on every pointer move across the whole gantt, and
   * re-rendering it at pointer rate to move one diamond would make the
   * schedule feel heavy. Placing a marker used to show nothing at all until
   * it existed, so the only way to find out where it would land was to drop
   * one and look.
   */
  const moveMilestoneGhost = (ev: React.PointerEvent) => {
    const el = msGhostRef.current;
    if (!el || !projectStart || !projectEnd) return;
    const frac = timelineFrac(ev.clientX);
    if (frac === null) return;
    const trackW = (ganttRef.current?.scrollWidth ?? 0) - LEFT_W;
    el.style.left = `${LEFT_W + frac * trackW}px`;
    el.style.opacity = "1";
    const when = new Date(projectStart.getTime() + frac * (projectEnd.getTime() - projectStart.getTime()));
    const label = el.querySelector<HTMLElement>("[data-ms-date]");
    if (label) label.textContent = fmtWeekLabel(when);
  };

  /**
   * Track the pointer with a hairline, so a bar can be read against the axis.
   *
   * A gantt asks you to compare a bar twelve rows down with a month label at
   * the top of the screen, across a gap with nothing in it. Everyone solves
   * that by holding a finger to the monitor. This is the finger.
   *
   * Written straight to the DOM rather than through state: this fires on every
   * pointer move over a surface with a row per scope, and re-rendering that
   * tree sixty times a second to move one line a pixel is how a gantt starts
   * to feel heavy. Nothing else on screen depends on where the pointer is, so
   * nothing else needs to know.
   */
  const moveCrosshair = (ev: React.PointerEvent) => {
    const el = crosshairRef.current;
    if (!el || !projectStart || !projectEnd) return;
    const frac = timelineFrac(ev.clientX);
    if (frac === null) { el.style.opacity = "0"; return; }
    const trackW = (ganttRef.current?.scrollWidth ?? 0) - LEFT_W;
    el.style.transform = `translateX(${LEFT_W + frac * trackW}px)`;
    el.style.opacity = "1";
    const when = new Date(projectStart.getTime() + frac * (projectEnd.getTime() - projectStart.getTime()));
    if (crosshairDateRef.current) crosshairDateRef.current.textContent = fmtWeekLabel(when);
  };

  const hideCrosshair = () => {
    const el = crosshairRef.current;
    if (el) el.style.opacity = "0";
  };

  const hideMilestoneGhost = () => {
    const el = msGhostRef.current;
    if (el) el.style.opacity = "0";
  };

  // ── milestone placement ────────────────────────────────────────────
  // Must account for horizontal scroll: ganttRef scrollLeft + click position
  // relative to the gantt container's left edge, minus the frozen LEFT_W column.
  const handleMilestoneClick = (e: React.MouseEvent) => {
    if (!milestoneMode || !projectStart || !projectEnd || !ganttRef.current) return;
    e.stopPropagation();
    const frac = timelineFrac(e.clientX);
    if (frac === null) return;
    const dateMs = projectStart.getTime() + frac * (projectEnd.getTime() - projectStart.getTime());
    const newMs: Milestone = { id: uid(), label: "Milestone", date: toISO(new Date(dateMs)) };
    setMilestones(prev => [...prev, newMs]);
    setSelectedMs(newMs.id);
    setMilestoneMode(false);
    hideMilestoneGhost();
    showToast("Milestone added — click the diamond to rename");
  };

  // ── print present view ─────────────────────────────────────────────
  const printSchedule = () => {
    const el = document.getElementById("oe-schedule-present");
    if (!el) { window.print(); return; }
    el.classList.add("oe-printing");
    const style = document.createElement("style");
    style.id = "oe-print-tmp";
    style.textContent = `@media print { @page { size: landscape; margin: .4in; } body > * { visibility: hidden !important; } #oe-schedule-present, #oe-schedule-present * { visibility: visible !important; } #oe-schedule-present { position: fixed; top: 0; left: 0; width: 100%; overflow: visible !important; } }`;
    document.head.appendChild(style);
    window.print();
    window.addEventListener("afterprint", () => {
      el.classList.remove("oe-printing");
      document.getElementById("oe-print-tmp")?.remove();
    }, { once: true });
  };

  // ── CSV export ─────────────────────────────────────────────────────
  const exportCSV = () => {
    const rows = ["Package,Division,Subcategory,Start Date,End Date,Duration (days),Cost,Confidence"];
    divisions.forEach(div => {
      div.subcategories.forEach(sub => {
        const cost = subTotals[sub.id] || 0;
        if (cost === 0) return;
        const entry = subRollup(sub.id);
        if (!entry) return;
        const pid = dominantProposalId(sub, itemState);
        const pkg = proposals.find(p => p.id === pid)?.name || "Unassigned";
        const dur = Math.round(daysBetween(fromISO(entry.start), fromISO(entry.end)));
        const unc = subUncertainty[sub.id] || 0;
        const conf = unc < 0.1 ? "Hard Bid" : unc < 0.5 ? "Mixed" : "Allowance/NR";
        const row = [
          `"${pkg}"`, `"${div.code} ${div.name}"`, `"${sub.name}"`,
          entry.start, entry.end, dur, cost.toFixed(0), conf,
        ].join(",");
        rows.push(row);
      });
    });
    // Add milestones section
    if (milestones.length > 0) {
      rows.push(""); rows.push("Milestones"); rows.push("Label,Date");
      milestones.forEach(m => rows.push(`"${m.label}",${m.date}`));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `OpenEstimate_Schedule_${toISO(new Date())}.csv`; a.click();
    URL.revokeObjectURL(url);
    showToast("Schedule exported as CSV — open in Excel or import to P6");
  };

  // ── ticks & gantt sizing ───────────────────────────────────────────
  const ticks     = projectStart && projectEnd ? getTicks(timeScale, projectStart, projectEnd) : [];
  const ganttMinW = projectStart && projectEnd ? ganttMinWidth(timeScale, projectStart, projectEnd) : 400;
  const border    = `1px solid ${T.borderSecondary}`;
  const gridLine  = `1px solid ${T.borderSecondary}22`;
  const activeDivs = divisions.filter(d => (divMeta[d.id]?.tot || 0) > 0);
  // Counted against subcategories specifically. costSchedule is keyed by
  // division, subcategory AND line item now, so counting its entries would
  // read "27/26 scheduled" the moment anyone scheduled a single line item.
  const { scheduledCount, totalSubCount, itemLevelCount } = React.useMemo(() => {
    const subIds = new Set<string>();
    const divIds = new Set<string>();
    let scheduled = 0, total = 0;
    divisions.forEach(d => {
      divIds.add(d.id);
      d.subcategories.forEach(sub => {
        subIds.add(sub.id);
        if ((subTotals[sub.id] || 0) <= 0) return;
        total++;
        if (resolveEntry([sub.id, d.id], costSchedule)) scheduled++;
      });
    });
    const items = Object.keys(costSchedule)
      .filter(id => !subIds.has(id) && !divIds.has(id) && costSchedule[id]?.bands?.length).length;
    return { scheduledCount: scheduled, totalSubCount: total, itemLevelCount: items };
  }, [divisions, subTotals, costSchedule]);

  // ── no start date ──────────────────────────────────────────────────
  if (!schedule.projectStartDate) {
    return (
      <div style={{ padding: "80px 36px", textAlign: "center", fontFamily: FONT }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 24 }}>Set a start date and duration to begin</div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10, background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 6, padding: "14px 20px" }}>
          <label style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".06em", textTransform: "uppercase" as any }}>Start</label>
          <input type="date" value={schedule.projectStartDate || ""}
            onChange={e => setSchedule(p => ({ ...p, projectStartDate: e.target.value }))}
            style={{ background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, color: T.textPrimary, fontSize: 12, padding: "5px 8px", fontFamily: FONT }} />
          <label style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".06em", textTransform: "uppercase" as any, marginLeft: 8 }}>Duration</label>
          <input type="number" min={1} value={duration || 12}
            onChange={e => setDuration(Math.max(1, parseInt(e.target.value) || 12))}
            title="Project duration -- the same number as Project Info and the printed estimate"
            style={{ background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, color: T.textPrimary, fontSize: 12, padding: "5px 8px", width: 52, fontFamily: FONT }} />
          <span style={{ fontSize: 11, color: T.textWeak }}>months</span>
        </div>
      </div>
    );
  }

  // ── column header label ────────────────────────────────────────────
  const colHeaderLabel = groupMode === "package" ? "Package"
    : groupMode === "phase" ? "Phase"
    : detail === "groups" ? "Division" : "Subcategory";

  // ── shared bar renderer ──────────────────────────────────────────
  // One row, however many bands. A split does not add a row -- both halves
  // stay on the line they belong to, which is the point: the row is the
  // scope, the bands are when it happens.
  //
  // `parentIds` is the rest of the resolution chain. A row with no schedule
  // of its own draws its parent's bands faintly; dragging or splitting one
  // adopts them, and from then on the row is scheduled independently.
  const renderBars = (
    nodeId: string, nodeName: string, parentIds: string[],
    color: string, isSel: boolean, uncertaintyKey?: string,
  ) => {
    const { bands, own } = effectiveBands(nodeId, parentIds);
    const unc = subUncertainty[uncertaintyKey ?? nodeId] || 0;
    const uncertain = showConfidence && unc > 0.3;
    const confTip = uncertain ? ` · ${Math.round(unc * 100)}% Allowance/NR` : "";

    if (bands.length === 0) {
      // Hovering previews the bar a drag would leave, where it would leave it.
      // The row used to carry the words "drag to schedule" instead, which said
      // the same thing once and then went on saying it on every unscheduled
      // row at once -- a paragraph of instructions where a schedule should be.
      return (
        <div
          className={milestoneMode ? undefined : "gantt-empty"}
          onPointerDown={e => onEmptyDown(e, nodeId)}
          onPointerMove={moveGhost}
          onPointerLeave={hideGhost}
          onPointerCancel={hideGhost}
          title={`${nodeName} — drag across this row to schedule it`}
          style={{ position: "absolute", inset: 0, cursor: milestoneMode ? "crosshair" : undefined }}>
          <div className="gantt-ghost" aria-hidden
            style={{
              width: `${ghostWidthPct}%`,
              background: `${color}2e`,
              border: `1px dashed ${color}99`,
            }} />
        </div>
      );
    }

    // Solid fills, like the phase timeline on Cash Flow, rather than the 19%
    // wash this used to have. An inherited bar stays pale on purpose -- that
    // is the one place the weight is carrying meaning rather than decoration.
    const barBg = !own
      ? `${color}1f`
      : uncertain
        ? `repeating-linear-gradient(45deg, ${color} 0 5px, ${color}88 5px 10px)`
        : color;
    const barBorder = `1px ${uncertain ? "dashed" : own ? "solid" : "dotted"} ${own ? color : color + "44"}`;

    return (
      <>
        {bands.map((band, i) => {
          const left = barL(band.start), width = barW(band.start, band.end);
          const share = bands.length > 1 ? ` · ${Math.round(band.pct)}% of cost` : "";
          return (
            <div key={`${band.start}_${i}`}
              onPointerDown={e => onBarDown(e, nodeId, parentIds, i, "move")}
              onClick={e => e.stopPropagation()}
              onDoubleClick={e => {
                e.stopPropagation();
                // Split where the pointer is, so the halves land where the user
                // means them to rather than always at the midpoint.
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const frac = rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0.5;
                const days = daysBetween(fromISO(band.start), fromISO(band.end));
                splitAt(nodeId, parentIds, i, toISO(addDays(fromISO(band.start), Math.round(days * frac))));
              }}
              title={`${nodeName} · ${band.start} → ${band.end}${share}${confTip}${own ? "" : " · inherited — drag to schedule separately"}\nDouble-click to split`}
              style={{
                position: "absolute",
                left: `${left}%`, width: `${width}%`,
                top: "50%", transform: "translateY(-50%)",
                height: 18, borderRadius: 3,
                background: barBg, border: barBorder,
                opacity: own ? (isSel ? 1 : .9) : 1,
                boxShadow: own ? `0 1px 3px ${color}44` : "none",
                cursor: "grab", userSelect: "none", boxSizing: "border-box",
                display: "flex", alignItems: "center",
                paddingLeft: 5, paddingRight: 6, overflow: "hidden",
              }}>
              {barLabel !== "none" && (
                <span style={{
                  fontSize: 10, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden",
                  textOverflow: "ellipsis", flex: 1,
                  color: own ? "#0d1117" : color,
                  textShadow: own ? "0 1px 1px rgba(255,255,255,.25)" : "none",
                }}>
                  {getBarLabelText(band.start, band.end)}
                </span>
              )}
              <div onPointerDown={e => onBarDown(e, nodeId, parentIds, i, "resize")}
                style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 5, cursor: "ew-resize", background: own ? "rgba(0,0,0,.25)" : `${color}55`, borderRadius: "0 3px 3px 0", flexShrink: 0 }} />
            </div>
          );
        })}

        {/* Date popover for the first band of the selected row */}
        {isSel && bands[0] && (
          <div onClick={e => e.stopPropagation()}
            style={{
              position: "absolute",
              left: `${barL(bands[0].start)}%`, top: "calc(100% + 2px)",
              zIndex: Z_BAR_POPOVER, background: T.bgModal,
              border: `1px solid ${T.borderSecondary}`,
              borderRadius: 6, padding: "8px 10px",
              display: "flex", gap: 8, alignItems: "flex-end",
              boxShadow: "0 4px 16px rgba(0,0,0,.35)", minWidth: 340,
            }}>
            {(() => {
              const b0 = bands[0];
              const cur = durationIn(fromISO(b0.start), fromISO(b0.end), durUnit);
              // The estimate often already knows the duration: supervision is
              // priced as 18 MO, a crane as 6 WK. Offer it rather than making
              // the user work it out from a calendar.
              const li = itemState[nodeId];
              const estUnit = timeUnitOf(li?.unit);
              const estQty = estUnit && Number(li?.qty) > 0 ? Number(li.qty) : null;
              const field = (f: "start" | "end") => (
                <div key={f} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <label style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em" }}>{f === "start" ? "START" : "END"}</label>
                  <input type="date" value={b0[f]}
                    onChange={e => updateDate(nodeId, parentIds, 0, f, e.target.value)}
                    style={{ background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, color: T.textPrimary, fontSize: 11, padding: "2px 4px", fontFamily: FONT }} />
                </div>
              );
              return (
                <>
                  {field("start")}
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <label style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em" }}>DURATION</label>
                    <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
                      <input type="number" min={0.5} step={durUnit === "days" ? 1 : 0.5} value={cur}
                        onChange={e => setBandDuration(nodeId, parentIds, 0, parseFloat(e.target.value), durUnit)}
                        title="Sets the end date from the start"
                        style={{ width: 54, background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, color: T.textPrimary, fontSize: 11, padding: "2px 4px", fontFamily: FONT }} />
                      <select value={durUnit} onChange={e => setDurUnit(e.target.value as DurUnit)}
                        style={{ background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, color: T.textPrimary, fontSize: 11, padding: "2px 3px", fontFamily: FONT }}>
                        <option value="days">days</option>
                        <option value="weeks">wks</option>
                        <option value="months">mo</option>
                      </select>
                    </div>
                    {estQty !== null && estUnit && (
                      <button
                        onClick={() => { setDurUnit(estUnit); setBandDuration(nodeId, parentIds, 0, estQty, estUnit); }}
                        title={`This line item is priced as ${estQty} ${li.unit} -- use that as its duration`}
                        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: FONT, fontSize: 10, color: T.borderAccent, textAlign: "left", textDecoration: "underline dotted" }}>
                        {estQty} {estUnit} from estimate
                      </button>
                    )}
                  </div>
                  {field("end")}
                </>
              );
            })()}
            <button
              onClick={() => {
                const b = bands[0];
                const days = daysBetween(fromISO(b.start), fromISO(b.end));
                splitAt(nodeId, parentIds, 0, toISO(addDays(fromISO(b.start), Math.round(days / 2))));
              }}
              title="Split this bar in two — both halves stay on this row"
              style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textSecondary, cursor: "pointer", fontSize: 10, borderRadius: 3, padding: "3px 7px", fontFamily: FONT }}>
              Split
            </button>
            {own && (
              <button onClick={() => { scheduleSnapshot(); writeBands(nodeId, []); }}
                title="Follow the parent's schedule again"
                style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textWeak, cursor: "pointer", fontSize: 10, borderRadius: 3, padding: "3px 7px", fontFamily: FONT }}>
                Reset
              </button>
            )}
            <button onClick={() => setSelectedSubId(null)}
              style={{ background: "none", border: "none", color: T.textWeak, cursor: "pointer", fontSize: 16, lineHeight: 1, paddingBottom: 2 }}>×</button>
          </div>
        )}
      </>
    );
  };

  /**
   * A subcategory row. Normally its own (or inherited) bands, draggable.
   *
   * Once any line item under it is scheduled in its own right, the row stops
   * being the thing that decides when the work happens and becomes a readout
   * of its items -- so it draws the union of them and is not draggable.
   * Dragging it would otherwise appear to move items it no longer governs.
   */
  const renderSubBar = (sub: Subcategory, color: string, isSel: boolean, divId?: string) => {
    const leaves = leavesBySub[sub.id] || [];
    if (!hasOwnLeafSchedule(leaves, costSchedule)) {
      return renderBars(sub.id, sub.name, divId ? [divId] : [], color, isSel);
    }
    const span = subRollup(sub.id);
    if (!span) return renderBars(sub.id, sub.name, divId ? [divId] : [], color, isSel);
    const n = leaves.filter(l => costSchedule[l.id]?.bands?.length).length;
    const isOpen = expandedSubsSched.has(sub.id);
    return (
      <div
        onClick={e => {
          e.stopPropagation();
          setExpandedSubsSched(pv => { const nx = new Set(pv); nx.has(sub.id) ? nx.delete(sub.id) : nx.add(sub.id); return nx; });
        }}
        title={`${sub.name} · ${span.start} → ${span.end}\nSpans ${n} line item${n === 1 ? "" : "s"} scheduled separately — ${isOpen ? "edit them on the rows below" : "click to open them"}`}
        style={{
          position: "absolute",
          left: `${barL(span.start)}%`, width: `${barW(span.start, span.end)}%`,
          top: "50%", transform: "translateY(-50%)",
          height: 10, borderRadius: 999,
          background: `${color}55`, border: `1px solid ${color}`,
          boxSizing: "border-box", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          paddingRight: 5,
        }}>
        <span style={{ fontSize: 10, color: T.textPrimary, fontWeight: 700, lineHeight: 1 }}>{n}</span>
      </div>
    );
  };

  /** Costed line items of a subcategory, in estimate order. */
  const schedItems = (sub: any) =>
    (leavesBySub[sub.id] || []).map(l => ({
      id: l.id,
      name: itemState[l.id]?.description || "Line item",
      cost: l.cost,
    }));

  /** Rows for the line items of one subcategory, when it is expanded. */
  const itemRowsFor = (sub: any, divId: string, color: string) => {
    if (!expandedSubsSched.has(sub.id)) return null;
    const items = schedItems(sub);
    if (items.length === 0) {
      return (
        <div key={`${sub.id}_noitems`} style={{ display: "flex", height: ROW_H }}>
          <div style={{ width: LEFT_W, flexShrink: 0, position: "sticky", left: 0, zIndex: Z_LEFT_COL, background: T.bgApp, borderBottom: `1px solid ${T.borderSecondary}12`, borderRight: border, display: "flex", alignItems: "center", paddingLeft: 34 }}>
            <span style={{ fontSize: 10, color: `${T.textWeak}88`, fontStyle: "italic" }}>no costed line items</span>
          </div>
          <div style={{ flex: 1, background: T.bgApp, borderBottom: `1px solid ${T.borderSecondary}12` }} />
        </div>
      );
    }
    return items.map(it => {
      const isSel = selectedSubId === it.id;
      const scheduled = !!costSchedule[it.id]?.bands?.length;
      return (
        <div key={it.id} style={{ display: "flex", height: ROW_H }}>
          <div
            onClick={() => setSelectedSubId(p => (p === it.id ? null : it.id))}
            style={{
              width: LEFT_W, flexShrink: 0, position: "sticky", left: 0, zIndex: Z_LEFT_COL,
              background: tintedOver(T.bgApp, isSel ? `${color}14` : null),
              borderBottom: `1px solid ${T.borderSecondary}12`, borderRight: border,
              display: "flex", alignItems: "center", cursor: "pointer", paddingLeft: 34,
            }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10, color: scheduled ? T.textSecondary : T.textWeak, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                {scheduled && <span title="Scheduled separately from its subcategory" style={{ color, marginRight: 5 }}>•</span>}
                {it.name}
              </div>
            </div>
            {showCost && <div style={{ fontSize: 10, color: T.textWeak, paddingRight: 10, flexShrink: 0 }}>{fmt(it.cost)}</div>}
          </div>
          <div style={{ flex: 1, position: "relative", background: isSel ? tintedOver(T.bgApp, `${color}14`) : T.bgApp, borderBottom: `1px solid ${T.borderSecondary}12`, overflow: "visible" }}>
            {ticks.map(t => <div key={t.label} style={{ position: "absolute", left: `${t.pct}%`, top: 0, bottom: 0, borderLeft: gridLine }} />)}
            {milestoneLinesInRow}
            {renderBars(it.id, it.name, [sub.id, divId], color, isSel, sub.id)}
          </div>
        </div>
      );
    });
  };

  // ── milestone dashed lines for a Gantt row cell ────────────────────
  /** The header's height, which grows to make room for milestone names. */
  const msBand  = (milestones.length > 0 || milestoneMode) ? MS_BAND : 0;
  const headerH = HEADER_H + msBand;

  const milestoneLinesInRow = milestones.map(m => projectStart ? (
    <div key={m.id} style={{
      position: "absolute", left: `${barL(m.date)}%`,
      top: 0, bottom: 0,
      borderLeft: `1px dashed ${T.borderAccent}55`,
      pointerEvents: "none", zIndex: 2,
    }} />
  ) : null);

  // ══════════════════════════════════════════════════════════════════
  //  TOOLBAR
  // ══════════════════════════════════════════════════════════════════
  const toolbar = (
    <div style={{ padding: isMobile ? "8px 12px" : "10px 16px", borderBottom: border, flexShrink: 0, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, background: T.bgPanel }}>

      {/* Title */}
      <div style={{ minWidth: 100, marginRight: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary }}>Schedule</div>
        {showTools && (
          <div style={{ fontSize: 10, color: T.textWeak, marginTop: 1 }}>
            {scheduledCount}/{totalSubCount} scheduled
            {itemLevelCount > 0 && (
              <span title="Line items scheduled separately from their subcategory">
                {" · "}{itemLevelCount} line item{itemLevelCount === 1 ? "" : "s"}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Date/duration */}
      {showTools && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
          <label style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".06em" }}>START</label>
          <input type="date" value={schedule.projectStartDate || ""}
            onChange={e => setSchedule(p => ({ ...p, projectStartDate: e.target.value }))}
            style={{ background: T.bgInput, border, borderRadius: 3, color: T.textPrimary, fontSize: 11, padding: "3px 5px", fontFamily: FONT }} />
          <label style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".06em" }}>DUR</label>
          <input type="number" min={1} value={duration || 12}
            onChange={e => setDuration(Math.max(1, parseInt(e.target.value) || 12))}
            title="Project duration -- the same number as Project Info and the printed estimate"
            style={{ background: T.bgInput, border, borderRadius: 3, color: T.textPrimary, fontSize: 11, padding: "3px 5px", width: 42, fontFamily: FONT }} />
          <span style={{ fontSize: 10, color: T.textWeak }}>mo</span>
        </div>
      )}

      <div style={{ flex: 1 }} />

      {/* Phases decide bar colour, grouping, and the order Auto-Distribute
          lays work out in -- but they are edited in Settings, which used to
          mean leaving the project, going through the picker, hunting the
          sidebar, and coming back. The link belongs where you realise the
          grouping is wrong. The settings header's back button returns here. */}
      {showTools && (
        <button onClick={() => setActiveTab("settings-phase-groups")}
          title={phaseGroups.length > 0
            ? `Edit the ${phaseGroups.length} phase${phaseGroups.length === 1 ? "" : "s"} that group and sequence this schedule`
            : "No phases yet -- add them to group and sequence this schedule"}
          style={{ background: "none", border, color: T.textMuted, fontFamily: FONT, fontSize: 11, padding: "4px 10px", minHeight: 24, borderRadius: 3, cursor: "pointer" }}>
          Edit Phases
        </button>
      )}

      {/* Auto-distribute */}
      {showTools && (
        <button onClick={handleAutoDistribute}
          style={{ background: T.bgInput, border, color: T.textSecondary, fontFamily: FONT, fontSize: 11, fontWeight: 600, padding: "4px 10px", minHeight: 24, borderRadius: 3, cursor: "pointer" }}>
          Auto-Distribute
        </button>
      )}

      {/* Milestone button */}
      {showTools && (
        <button
          onClick={() => setMilestoneMode(p => !p)}
          title={milestoneMode ? "Click the timeline to place milestone" : "Add a milestone marker"}
          style={{
            background: milestoneMode ? T.borderAccent : T.bgInput,
            border: milestoneMode ? `1px solid ${T.borderAccent}` : border,
            color: milestoneMode ? "#0a0f1a" : T.textSecondary,
            fontFamily: FONT, fontSize: 11, fontWeight: 600,
            padding: "4px 10px", minHeight: 24, borderRadius: 3, cursor: "pointer",
          }}>
          {milestoneMode ? "↖ Click timeline…" : "◆ Milestone"}
        </button>
      )}

      {/* Export CSV */}
      <button onClick={exportCSV}
        style={{ background: T.bgInput, border, color: T.textSecondary, fontFamily: FONT, fontSize: 11, fontWeight: 600, padding: "4px 10px", minHeight: 24, borderRadius: 3, cursor: "pointer" }}>
        Export CSV
      </button>

      {/* Print — prints exactly what the switches below are showing */}
      {(
        <button onClick={printSchedule}
          style={{ background: T.borderAccent, border: `1px solid ${T.borderAccent}`, color: "#0a0f1a", fontFamily: FONT, fontSize: 11, fontWeight: 700, padding: "4px 12px", minHeight: 24, borderRadius: 3, cursor: "pointer" }}>
          Print / PDF
        </button>
      )}

      {/* Package / Phase / Division toggle */}
      <SegControl
        options={[{ v: "package", label: "Package" }, { v: "phase", label: "Phase" }, { v: "division", label: "Division" }]}
        value={groupMode} onChange={v => setGroupMode(v as GroupMode)}
        T={T} border={border} accent={ACCENT} />

      {/* Time scale */}
      <SegControl
        options={[{ v: "week", label: "Wk" }, { v: "month", label: "Mo" }, { v: "quarter", label: "Qtr" }]}
        value={timeScale} onChange={v => setTimeScale(v as TimeScale)}
        T={T} border={border} accent={ACCENT} />

      {/* Display — everything about how the schedule reads, in one place */}
      <div style={{ position: "relative", flexShrink: 0 }}>
        <button onClick={() => setDisplayOpen(o => !o)}
          title="What the schedule shows — detail, labels, colour"
          style={{
            background: displayOpen ? `${ACCENT}22` : T.bgInput,
            border: displayOpen ? `1px solid ${ACCENT}88` : border,
            color: displayOpen ? ACCENT : T.textSecondary,
            fontFamily: FONT, fontSize: 11, fontWeight: 600,
            padding: "4px 10px", minHeight: 24, borderRadius: 3, cursor: "pointer",
          }}>
          Display
        </button>
        {displayOpen && (
          <>
            <div onClick={() => setDisplayOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 199 }} />
            <div style={{
              position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 200,
              background: T.bgModal, border: `1px solid ${T.borderSecondary}`, borderRadius: 6,
              boxShadow: "0 8px 28px rgba(0,0,0,.45)", padding: 12, width: 250,
              display: "flex", flexDirection: "column", gap: 12,
            }}>
              <div>
                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 5 }}>Detail</div>
                <SegControl
                  options={[{ v: "groups", label: "Groups" }, { v: "subs", label: "Scope" }, { v: "items", label: "Line items" }]}
                  value={detail} onChange={v => setDetail(v as Detail)}
                  T={T} border={border} accent={ACCENT} />
              </div>
              <div>
                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 5 }}>Dates on bars</div>
                <SegControl
                  options={[{ v: "none", label: "Off" }, { v: "start", label: "Start" }, { v: "end", label: "End" }, { v: "range", label: "Both" }]}
                  value={barLabel} onChange={v => setBarLabel(v as BarLabel)}
                  T={T} border={border} accent={ACCENT} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7, borderTop: `1px solid ${T.borderPrimary}`, paddingTop: 10 }}>
                {([
                  ["Costs", showCost, setShowCost, "Show the cost column and totals"],
                  ["Title block", showTitleBlock, setShowTitleBlock, "Project name, date range and total above the bars — for a printed page"],
                  ["Confidence hatching", showConfidence, setShowConfidence, "Hatch bars that are more than 30% Allowance or Needs Review"],
                  ["Monochrome", mono, setMono, "Drop the colour coding — prints better in black and white"],
                  ["Editing tools", showTools, setShowTools, "Start date, duration, Auto-Distribute and milestones"],
                ] as const).map(([label, val, set, tip]) => (
                  <label key={label} title={tip}
                    style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 11, color: T.textSecondary }}>
                    <input type="checkbox" checked={val}
                      onChange={e => (set as (v: boolean) => void)(e.target.checked)}
                      style={{ accentColor: ACCENT, cursor: "pointer" }} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════
  //  SHARED GANTT RENDERER
  // ══════════════════════════════════════════════════════════════════
  /**
   * The spend these bands add up to, drawn under them.
   *
   * Not a second chart of the same data — the same axis. The point is the
   * alignment: the hump sits under the bands that cause it, so dragging a
   * band and watching the hump move is the whole explanation of what the
   * schedule is for.
   */
  const cashCurve = (!projectStart || !projectEnd || cashWeeks.length === 0 || !showCost) ? null : (() => {
    const H = 54, PAD = 6;
    const max = Math.max(...cashWeeks, 1);
    const n = cashWeeks.length;
    const x = (i: number) => (i / Math.max(1, n - 1)) * ganttMinW;
    const y = (v: number) => PAD + (1 - v / max) * (H - PAD * 2);
    const line = cashWeeks.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
    const area = `${0},${H} ${line} ${ganttMinW},${H}`;

    // A dollar figure anybody quotes is a month, not a week, so both the peak
    // and the readout are the four weeks running from the point in question.
    const monthAt = (i: number) => cashWeeks.slice(i, i + 4).reduce((a, v) => a + v, 0);
    const peakX = x(Math.min(cashPeak.at + 2, n - 1));
    const hov = cashHover == null ? null : Math.max(0, Math.min(n - 1, cashHover));
    const hovX = hov == null ? 0 : x(hov);

    return (
      <div style={{ display: "flex", position: "sticky", bottom: 0, zIndex: Z_CASH_STRIP,
        borderTop: `1px solid ${T.borderPrimary}`, background: T.bgPanel,
        boxShadow: "0 -6px 14px rgba(0,0,0,.28)" }}>
        <div style={{ width: LEFT_W, flexShrink: 0, padding: "8px 12px", boxSizing: "border-box",
          position: "sticky", left: 0, zIndex: Z_CASH_STRIP + 1, background: T.bgPanel,
          borderRight: `1px solid ${T.borderPrimary}` }}>
          {/* The strip is a reading of the full tab, so it says where the
              full tab is. The reverse link already existed; this one did
              not, which made the relationship look one-way. */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase" }}>Cash flow</span>
            {onOpenCashflow && (
              <button onClick={onOpenCashflow} title="Open the full cash flow"
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                  fontFamily: FONT, fontSize: 10, color: T.borderAccent }}>
                open →
              </button>
            )}
          </div>
          {/* The peak is the resting figure; the pointer temporarily replaces it
              with the month it is over, so one line serves both and the strip
              does not grow a second row that is blank most of the time. */}
          <div style={{ fontSize: 11, marginTop: 3, fontVariantNumeric: "tabular-nums",
            color: hov == null ? T.textMuted : T.textSecondary, whiteSpace: "nowrap" }}>
            {hov == null
              ? <>Peak month {fmt(Math.round(cashPeak.amt))}</>
              : <>{fmtMonth(addDays(projectStart, hov * 7))} · {fmt(Math.round(monthAt(hov)))}</>}
          </div>
        </div>
        <div style={{ width: ganttMinW, flexShrink: 0, position: "relative" }}>
          <svg width={ganttMinW} height={H} style={{ display: "block" }}
            onPointerMove={e => {
              const r = e.currentTarget.getBoundingClientRect();
              setCashHover(Math.round(((e.clientX - r.left) / Math.max(1, r.width)) * (n - 1)));
            }}
            onPointerLeave={() => setCashHover(null)}>
            <polygon points={area} fill={T.borderAccent} fillOpacity={0.14} />
            <polyline points={line} fill="none" stroke={T.borderAccent} strokeWidth={1.5} />
            {/* What the top of the plot is worth. Without it the curve is a
                shape with no size, which reassures without informing. */}
            <text x={4} y={12} fontSize={10} fill={T.textWeak} style={{ fontFamily: FONT }}>
              {fmt(Math.round(max))}/wk
            </text>
            {cashPeak.amt > 0 && (
              <g>
                <line x1={peakX} y1={0} x2={peakX} y2={H} stroke={T.borderAccent} strokeOpacity={0.45}
                  strokeWidth={1} strokeDasharray="3,3" />
                {/* The figure, on the marker. It said only "peak", which
                    names the spike without answering the question the spike
                    raises -- and the amount was in the label column at the far
                    left of the row, which is the one place you are not looking
                    when you are looking at the hump.

                    Flipped to the left of the line when the peak falls near
                    the end of the schedule, so it is never clipped by the edge
                    of the track. */}
                <text x={peakX + (peakX > ganttMinW - 120 ? -6 : 6)} y={13} fontSize={10}
                  textAnchor={peakX > ganttMinW - 120 ? "end" : "start"}
                  fill={T.textMuted} style={{ fontFamily: FONT }}>
                  peak {fmt(Math.round(cashPeak.amt))}
                </text>
              </g>
            )}
            {hov != null && (
              <g pointerEvents="none">
                <line x1={hovX} y1={0} x2={hovX} y2={H} stroke={T.textMuted} strokeOpacity={0.55} strokeWidth={1} />
                <circle cx={hovX} cy={y(cashWeeks[hov])} r={2.5} fill={T.borderAccent} />
              </g>
            )}
          </svg>
        </div>
      </div>
    );
  })();

  const renderGantt = (rows: React.ReactNode) => (
    <div ref={ganttRef} id="oe-schedule-present" style={{ flex: 1, overflow: "auto", position: "relative", cursor: milestoneMode ? "crosshair" : undefined }}
      onPointerMove={e => { onMove(e); moveCrosshair(e); if (milestoneMode) moveMilestoneGhost(e); }}
      onPointerUp={onUp} onPointerLeave={() => { clearGhosts(); hideCrosshair(); }}
      onClick={milestoneMode ? handleMilestoneClick : undefined}>
      <div style={{ minWidth: LEFT_W + ganttMinW, position: "relative" }}>

        {/* The pointer's own gridline. Stretched the full height of the
            content so it crosses the bars and the cash curve alike -- the
            question it answers is "what is happening at this date", and the
            spend at that date is part of the answer.

            pointer-events:none, or it would sit between the cursor and every
            bar it is drawn over and nothing could be dragged. Positioned by
            transform rather than left so the browser can move it without
            recalculating layout for a table this tall. */}
        <div ref={crosshairRef} aria-hidden
          style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: 1,
            // Faint on purpose. At full strength it reads as a rule on the
            // chart -- a thing with meaning, competing with the bars -- rather
            // than as a cursor. It only has to be findable at the row the eye
            // is already on.
            background: `${T.textMuted}2e`, opacity: 0, pointerEvents: "none", zIndex: Z_CROSSHAIR }}>
          {/* Parked just clear of the frozen header rather than at top: 0.
              The crosshair sits below the header in the stacking order -- it
              has to, or it would paint over the frozen left column when the
              track is scrolled -- so a chip at the very top disappeared
              behind the month labels it was there to qualify. */}
          <div ref={crosshairDateRef}
            style={{ position: "sticky", top: headerH + 4, marginLeft: 5, display: "inline-block",
              fontSize: 10, fontFamily: FONT, color: T.textSecondary, background: T.bgPanel,
              border: `1px solid ${T.borderSecondary}`, borderRadius: 3, padding: "1px 5px",
              whiteSpace: "nowrap" }} />
        </div>

        {/* The marker a click would leave, where it would leave it. Drawn
            dashed and hollow so it reads as a proposal rather than as a
            milestone that is already there, and spanning the full height
            because what anyone is really checking is which bars it lands
            between. */}
        {milestoneMode && projectStart && projectEnd && (
          <div ref={msGhostRef} aria-hidden
            style={{
              position: "absolute", left: 0, top: 0, bottom: 0, width: 0,
              zIndex: Z_HEADER_ROW + 1, pointerEvents: "none",
              opacity: 0, transition: "opacity .12s ease",
            }}>
            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 1,
              borderLeft: `1px dashed ${T.borderAccent}aa` }} />
            <div style={{
              position: "absolute", top: headerH - 14, left: -5, width: 10, height: 10,
              transform: "rotate(45deg)", background: `${T.borderAccent}33`,
              border: `1px dashed ${T.borderAccent}`, boxSizing: "border-box",
            }} />
            <div data-ms-date
              style={{ position: "absolute", top: 2, left: 6, fontSize: 10,
                color: T.textSecondary, whiteSpace: "nowrap", fontWeight: 600 }} />
          </div>
        )}

        {/* Sticky header row */}
        <div style={{ display: "flex", position: "sticky", top: 0, zIndex: Z_HEADER_ROW }}>
          {/* Frozen left header */}
          <div style={{
            width: LEFT_W, flexShrink: 0,
            position: "sticky", left: 0, zIndex: Z_HEADER_LEFT,
            background: T.bgPanel, borderBottom: border, borderRight: border,
            height: headerH, display: "flex", alignItems: "center", paddingLeft: 12,
          }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase" }}>
              {colHeaderLabel}
            </span>
            {showCost && (
              <span style={{ fontSize: 10, fontWeight: 700, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase", marginLeft: "auto", paddingRight: 10 }}>Cost</span>
            )}
          </div>

          {/* Timeline header */}
          <div
            style={{
              flex: 1, position: "relative", height: headerH,
              background: T.bgPanel, borderBottom: border, overflow: "visible",
            }}>
            {projectStart && (
              <div style={{ position: "absolute", left: 5, top: msBand, bottom: 0, display: "flex", alignItems: "center", fontSize: 10, color: T.textWeak }}>
                {fmtMonth(projectStart)}
              </div>
            )}
            {ticks.map(t => (
              <div key={t.label} style={{ position: "absolute", left: `${t.pct}%`, top: msBand, bottom: 0, paddingLeft: 4, display: "flex", alignItems: "center", fontSize: 10, color: T.textSecondary, fontWeight: 600, borderLeft: `1px solid ${T.borderSecondary}55` }}>
                {t.label}
              </div>
            ))}
            {projectEnd && (
              <div style={{ position: "absolute", right: 5, top: msBand, bottom: 0, display: "flex", alignItems: "center", fontSize: 10, color: T.textWeak }}>
                {fmtMonth(projectEnd)}
              </div>
            )}
            {/* Milestone diamonds in header */}
            {milestones.map(m => {
              if (!projectStart) return null;
              const pct = barL(m.date);
              const isSel = selectedMs === m.id;
              return (
                <div key={m.id}
                  onPointerDown={e => { e.stopPropagation(); setSelectedMs(p => p === m.id ? null : m.id); }}
                  onClick={e => e.stopPropagation()}
                  style={{ position: "absolute", left: `${pct}%`, top: 0, height: "100%", zIndex: 15, cursor: "pointer" }}>
                  {/* Vertical line down */}
                  <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 1, background: `${T.borderAccent}77` }} />
                  {/* Diamond */}
                  <div style={{
                    position: "absolute", bottom: 4, left: -5,
                    width: 10, height: 10,
                    transform: "rotate(45deg)",
                    background: isSel ? T.borderAccent : `${T.borderAccent}bb`,
                    border: `1px solid ${T.borderAccent}`,
                    boxSizing: "border-box",
                  }} />
                  {/* Label */}
                  <div style={{ position: "absolute", top: 2, left: 6, fontSize: 10, color: T.textSecondary, whiteSpace: "nowrap", fontWeight: 600, pointerEvents: "none" }}>
                    {m.label}
                  </div>
                  {/* Edit popover */}
                  {isSel && (
                    <div onPointerDown={e => e.stopPropagation()}
                      style={{
                        position: "absolute", top: "calc(100% + 2px)", left: -70,
                        zIndex: 40, background: T.bgModal,
                        border: `1px solid ${T.borderAccent}`,
                        borderRadius: 6, padding: "6px 8px",
                        display: "flex", gap: 6, alignItems: "center",
                        boxShadow: "0 4px 12px rgba(0,0,0,.35)", minWidth: 190,
                      }}>
                      <input
                        value={m.label}
                        autoFocus
                        onFocus={() => scheduleSnapshot()}
                        onChange={e => setMilestones(prev => prev.map(x => x.id === m.id ? { ...x, label: e.target.value } : x), { snapshot: false })}
                        style={{ background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, color: T.textPrimary, fontSize: 10, fontFamily: FONT, padding: "2px 5px", flex: 1 }} />
                      <input
                        type="date" value={m.date}
                        onFocus={() => scheduleSnapshot()}
                        onChange={e => setMilestones(prev => prev.map(x => x.id === m.id ? { ...x, date: e.target.value } : x), { snapshot: false })}
                        style={{ background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, color: T.textPrimary, fontSize: 10, fontFamily: FONT, padding: "2px 3px", width: 110 }} />
                      <button
                        onClick={() => { setMilestones(prev => prev.filter(x => x.id !== m.id)); setSelectedMs(null); }}
                        title="Remove milestone"
                        style={{ background: "none", border: "none", color: "#fc8181", cursor: "pointer", fontSize: 13, lineHeight: 1, padding: 0, flexShrink: 0 }}>✕</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Data rows */}
        {rows}

        {/* ── The cash curve these bands produce ──
            Inside the scrolling track and offset by the same gutter, so it
            shares the bars' x-axis exactly and scrolls with them. */}
        {cashCurve}
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════
  //  TITLE BLOCK  (optional — for a printed page)
  // ══════════════════════════════════════════════════════════════════
  // What the old Present mode put at the top of the sheet. It is a header,
  // so it is a switch rather than a mode: turning it on does not take the
  // bars away from you.
  const grandTotal = activeDivs.reduce((n, d) => n + (divMeta[d.id]?.tot || 0), 0);

  const titleBlock = !showTitleBlock ? null : (
    <div style={{ display: "flex" }}>
      <div style={{
        width: LEFT_W, flexShrink: 0, position: "sticky", left: 0, zIndex: Z_LEFT_COL,
        background: T.bgModal, borderBottom: border, borderRight: border, padding: "14px 16px",
      }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.textPrimary, letterSpacing: "-.01em" }}>PROJECT SCHEDULE</div>
        {projectStart && projectEnd && (
          <div style={{ fontSize: 10, color: T.textWeak, marginTop: 3 }}>
            {fmtMonthYear(projectStart)} — {fmtMonthYear(projectEnd)}
          </div>
        )}
        {showCost && (
          <div style={{ fontSize: 13, fontWeight: 700, color: T.borderAccent, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
            {fmt(grandTotal)}
          </div>
        )}
      </div>
      <div style={{ flex: 1, background: T.bgModal, borderBottom: border, padding: "14px 0", position: "relative" }}>
        {milestoneLinesInRow}
      </div>
    </div>
  );

  const printFooter = !showTitleBlock ? null : (
    <div style={{ display: "flex" }}>
      <div style={{ width: LEFT_W, flexShrink: 0, position: "sticky", left: 0, zIndex: Z_LEFT_COL, background: T.bgModal, padding: "10px 16px" }} />
      <div style={{ flex: 1, background: T.bgModal, padding: "10px 12px" }}>
        <div style={{ fontSize: 10, color: T.textWeak, textAlign: "right" }}>
          Prepared with OpenEstimate · {new Date().toLocaleDateString("default", { month: "long", year: "numeric" })}
        </div>
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════
  //  EDIT MODE — DIVISION GROUPING
  // ══════════════════════════════════════════════════════════════════
  const editRowsByDivision = divisions.map(div => {
    const subs  = div.subcategories.filter(s => (subTotals[s.id] || 0) > 0);
    if (subs.length === 0) return null;
    const meta  = divMeta[div.id];
    const isExp = expandedDivs.has(div.id);
    const color = shade(meta.colorIdx >= 0 ? DIV_COLORS[meta.colorIdx % DIV_COLORS.length] : ACCENT);

    return (
      <React.Fragment key={div.id}>
        {/* Division header */}
        <div style={{ display: "flex", height: DIV_H }}>
          <div
            onClick={() => setExpandedDivs(p => { const s = new Set(p); s.has(div.id) ? s.delete(div.id) : s.add(div.id); return s; })}
            style={{ width: LEFT_W, flexShrink: 0, position: "sticky", left: 0, zIndex: Z_LEFT_COL, background: T.bgModal, borderBottom: border, borderRight: border, display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" }}
            onMouseOver={e => (e.currentTarget.style.background = `${T.borderPrimary}18`)}
            onMouseOut={e => (e.currentTarget.style.background = T.bgModal)}>
            <div style={{ width: 3, alignSelf: "stretch", background: color, flexShrink: 0 }} />
            <span style={{ fontSize: 10, color: T.textWeak, margin: "0 5px 0 8px", display: "inline-block", transition: "transform .15s", transform: isExp ? "rotate(90deg)" : "none" }}>▶</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textSecondary, letterSpacing: ".01em", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{div.code} — {div.name}</div>
            </div>
            {showCost && <div style={{ fontSize: 11, fontWeight: 700, color: T.textSecondary, fontVariantNumeric: "tabular-nums", paddingRight: 10, paddingLeft: 4, flexShrink: 0 }}>{fmt(meta.tot)}</div>}
          </div>
          <div style={{ flex: 1, position: "relative", background: T.bgModal, borderBottom: border, overflow: "hidden" }}>
            {ticks.map(t => <div key={t.label} style={{ position: "absolute", left: `${t.pct}%`, top: 0, bottom: 0, borderLeft: gridLine }} />)}
            {milestoneLinesInRow}
            {meta.spanStart && meta.spanEnd ? (
              <div style={{ position: "absolute", left: `${barL(toISO(meta.spanStart))}%`, width: `${barW(toISO(meta.spanStart), toISO(meta.spanEnd))}%`, top: "50%", transform: "translateY(-50%)", height: 10, borderRadius: 6, background: color, opacity: .85, boxSizing: "border-box" }} />
            ) : (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", paddingLeft: 10 }}>
                <span style={{ fontSize: 10, color: `${T.textWeak}44`, fontStyle: "italic" }}>not scheduled</span>
              </div>
            )}
          </div>
        </div>

        {/* Subcategory rows */}
        {isExp && subs.map(sub => {
          const isSel = selectedSubId === sub.id;
          const pkgColor = subPkgColor[sub.id];  // ★ package color tag

          return (
            <React.Fragment key={sub.id}>
            <div style={{ display: "flex", height: ROW_H }}>
              <div
                onClick={() => setSelectedSubId(p => p === sub.id ? null : sub.id)}
                style={{
                  width: LEFT_W, flexShrink: 0, position: "sticky", left: 0, zIndex: Z_LEFT_COL,
                  background: tintedOver(T.bgPanel, isSel ? `${color}14` : null),
                  borderBottom: `1px solid ${T.borderSecondary}15`, borderRight: border,
                  display: "flex", alignItems: "center", cursor: "pointer", paddingLeft: 18,
                }}
                onMouseOver={e => { if (!isSel) e.currentTarget.style.background = `${T.borderPrimary}0d`; }}
                onMouseOut={e => { if (!isSel) e.currentTarget.style.background = isSel ? `${color}14` : T.bgPanel; }}>
                {/* ★ Package color tag — 3px left stripe in proposal color */}
                <div style={{ width: 3, alignSelf: "stretch", background: pkgColor, flexShrink: 0, opacity: 0.7, marginRight: 0 }} />
                <span
                  onClick={e => { e.stopPropagation(); setExpandedSubsSched(pv => { const n = new Set(pv); n.has(sub.id) ? n.delete(sub.id) : n.add(sub.id); return n; }); }}
                  title="Show line items"
                  style={{ fontSize: 10, color: T.textWeak, padding: "0 4px", cursor: "pointer", display: "inline-block", transition: "transform .15s", transform: expandedSubsSched.has(sub.id) ? "rotate(90deg)" : "none", flexShrink: 0 }}>&#9654;</span>
                <div style={{ flex: 1, minWidth: 0, paddingLeft: 2 }}>
                  <div style={{ fontSize: 10, color: T.textTertiary, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{sub.name}</div>
                </div>
                {phaseChip(div.id, sub)}
                {showCost && <div style={{ fontSize: 10, color: T.textTertiary, fontVariantNumeric: "tabular-nums", paddingRight: 10, flexShrink: 0 }}>{fmt(subTotals[sub.id] || 0)}</div>}
              </div>
              <div style={{ flex: 1, position: "relative", background: isSel ? tintedOver(T.bgPanel, `${color}14`) : T.bgPanel, borderBottom: `1px solid ${T.borderSecondary}15`, overflow: "visible" }}>
                {ticks.map(t => <div key={t.label} style={{ position: "absolute", left: `${t.pct}%`, top: 0, bottom: 0, borderLeft: gridLine }} />)}
                {milestoneLinesInRow}
                {renderSubBar(sub, color, isSel, div.id)}
              </div>
            </div>
            {itemRowsFor(sub, div.id, color)}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  });

  // ══════════════════════════════════════════════════════════════════
  //  EDIT MODE — PACKAGE GROUPING
  // ══════════════════════════════════════════════════════════════════
  const editRowsByPackage = packageGroups.map(grp => {
    const isExp = expandedPkgs.has(grp.id);
    return (
      <React.Fragment key={grp.id}>
        {/* Package header */}
        <div style={{ display: "flex", height: DIV_H }}>
          <div
            onClick={() => setExpandedPkgs(p => { const s = new Set(p); s.has(grp.id) ? s.delete(grp.id) : s.add(grp.id); return s; })}
            style={{ width: LEFT_W, flexShrink: 0, position: "sticky", left: 0, zIndex: Z_LEFT_COL, background: T.bgModal, borderBottom: border, borderRight: border, display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" }}
            onMouseOver={e => (e.currentTarget.style.background = `${T.borderPrimary}18`)}
            onMouseOut={e => (e.currentTarget.style.background = T.bgModal)}>
            <div style={{ width: 3, alignSelf: "stretch", background: grp.colorHex, flexShrink: 0 }} />
            <span style={{ fontSize: 10, color: T.textWeak, margin: "0 5px 0 8px", display: "inline-block", transition: "transform .15s", transform: isExp ? "rotate(90deg)" : "none" }}>▶</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: T.textSecondary, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{grp.name}</div>
              <div style={{ fontSize: 10, color: T.textWeak, marginTop: 1 }}>{grp.subs.length} scope item{grp.subs.length === 1 ? "" : "s"}</div>
            </div>
            {showCost && <div style={{ fontSize: 10, fontWeight: 700, color: T.textSecondary, paddingRight: 10, paddingLeft: 4, flexShrink: 0 }}>{fmt(grp.totalCost)}</div>}
          </div>
          <div style={{ flex: 1, position: "relative", background: T.bgModal, borderBottom: border, overflow: "hidden" }}>
            {ticks.map(t => <div key={t.label} style={{ position: "absolute", left: `${t.pct}%`, top: 0, bottom: 0, borderLeft: gridLine }} />)}
            {milestoneLinesInRow}
            {grp.spanStart && grp.spanEnd ? (
              <div style={{ position: "absolute", left: `${barL(toISO(grp.spanStart))}%`, width: `${barW(toISO(grp.spanStart), toISO(grp.spanEnd))}%`, top: "50%", transform: "translateY(-50%)", height: 10, borderRadius: 6, background: grp.colorHex, opacity: .85, boxSizing: "border-box" }} />
            ) : (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", paddingLeft: 10 }}>
                <span style={{ fontSize: 10, color: `${T.textWeak}44`, fontStyle: "italic" }}>not scheduled</span>
              </div>
            )}
          </div>
        </div>

        {/* Subcategory rows under package */}
        {isExp && grp.subs.map(({ div, sub, cost }) => {
          const isSel = selectedSubId === sub.id;
          return (
            <React.Fragment key={sub.id}>
            <div style={{ display: "flex", height: ROW_H }}>
              <div
                onClick={() => setSelectedSubId(p => p === sub.id ? null : sub.id)}
                style={{
                  width: LEFT_W, flexShrink: 0, position: "sticky", left: 0, zIndex: Z_LEFT_COL,
                  background: tintedOver(T.bgPanel, isSel ? `${grp.colorHex}14` : null),
                  borderBottom: `1px solid ${T.borderSecondary}15`, borderRight: border,
                  display: "flex", alignItems: "center", cursor: "pointer", paddingLeft: 20,
                }}
                onMouseOver={e => { if (!isSel) e.currentTarget.style.background = `${T.borderPrimary}0d`; }}
                onMouseOut={e => { if (!isSel) e.currentTarget.style.background = isSel ? `${grp.colorHex}14` : T.bgPanel; }}>
                <span
                  onClick={e => { e.stopPropagation(); setExpandedSubsSched(pv => { const n = new Set(pv); n.has(sub.id) ? n.delete(sub.id) : n.add(sub.id); return n; }); }}
                  title="Show line items"
                  style={{ fontSize: 10, color: T.textWeak, padding: "0 4px", cursor: "pointer", display: "inline-block", transition: "transform .15s", transform: expandedSubsSched.has(sub.id) ? "rotate(90deg)" : "none", flexShrink: 0 }}>&#9654;</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, color: T.textTertiary, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{sub.name}</div>
                  <div style={{ fontSize: 10, color: T.textWeak, marginTop: 1 }}>{div.code}</div>
                </div>
                {showCost && <div style={{ fontSize: 10, color: T.textTertiary, fontVariantNumeric: "tabular-nums", paddingRight: 10, flexShrink: 0 }}>{fmt(cost)}</div>}
              </div>
              <div style={{ flex: 1, position: "relative", background: isSel ? tintedOver(T.bgPanel, `${grp.colorHex}14`) : T.bgPanel, borderBottom: `1px solid ${T.borderSecondary}15`, overflow: "visible" }}>
                {ticks.map(t => <div key={t.label} style={{ position: "absolute", left: `${t.pct}%`, top: 0, bottom: 0, borderLeft: gridLine }} />)}
                {milestoneLinesInRow}
                {renderSubBar(sub, shade(grp.colorHex), isSel, div.id)}
              </div>
            </div>
            {itemRowsFor(sub, div.id, shade(grp.colorHex))}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  });

  // ══════════════════════════════════════════════════════════════════
  //  EDIT MODE — PHASE GROUP GROUPING
  // ══════════════════════════════════════════════════════════════════
  const editRowsByPhase = phaseGroupRows.map(grp => {
    const isExp = expandedPkgs.has(grp.id);
    return (
      <React.Fragment key={grp.id}>
        {/* Phase group header */}
        <div style={{ display: "flex", height: DIV_H }}>
          <div
            onClick={() => setExpandedPkgs(p => { const s = new Set(p); s.has(grp.id) ? s.delete(grp.id) : s.add(grp.id); return s; })}
            style={{ width: LEFT_W, flexShrink: 0, position: "sticky", left: 0, zIndex: Z_LEFT_COL, background: T.bgModal, borderBottom: border, borderRight: border, display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" }}
            onMouseOver={e => (e.currentTarget.style.background = `${T.borderPrimary}18`)}
            onMouseOut={e => (e.currentTarget.style.background = T.bgModal)}>
            <div style={{ width: 3, alignSelf: "stretch", background: grp.colorHex, flexShrink: 0 }} />
            <span style={{ fontSize: 10, color: T.textWeak, margin: "0 5px 0 8px", display: "inline-block", transition: "transform .15s", transform: isExp ? "rotate(90deg)" : "none" }}>▶</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: T.textSecondary, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{grp.name}</div>
              <div style={{ fontSize: 10, color: T.textWeak, marginTop: 1 }}>{grp.subs.length} scope item{grp.subs.length === 1 ? "" : "s"}</div>
            </div>
            {showCost && <div style={{ fontSize: 10, fontWeight: 700, color: T.textSecondary, paddingRight: 10, paddingLeft: 4, flexShrink: 0 }}>{fmt(grp.totalCost)}</div>}
          </div>
          <div style={{ flex: 1, position: "relative", background: T.bgModal, borderBottom: border, overflow: "hidden" }}>
            {ticks.map(t => <div key={t.label} style={{ position: "absolute", left: `${t.pct}%`, top: 0, bottom: 0, borderLeft: gridLine }} />)}
            {milestoneLinesInRow}
            {grp.spanStart && grp.spanEnd ? (
              <div style={{ position: "absolute", left: `${barL(toISO(grp.spanStart))}%`, width: `${barW(toISO(grp.spanStart), toISO(grp.spanEnd))}%`, top: "50%", transform: "translateY(-50%)", height: 10, borderRadius: 6, background: grp.colorHex, opacity: .85, boxSizing: "border-box" }} />
            ) : (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", paddingLeft: 10 }}>
                <span style={{ fontSize: 10, color: `${T.textWeak}44`, fontStyle: "italic" }}>not scheduled</span>
              </div>
            )}
          </div>
        </div>

        {/* Subcategory rows under phase */}
        {isExp && grp.subs.map(({ div, sub, cost }) => {
          const isSel = selectedSubId === sub.id;
          return (
            <React.Fragment key={sub.id}>
            <div style={{ display: "flex", height: ROW_H }}>
              <div
                onClick={() => setSelectedSubId(p => p === sub.id ? null : sub.id)}
                style={{
                  width: LEFT_W, flexShrink: 0, position: "sticky", left: 0, zIndex: Z_LEFT_COL,
                  background: tintedOver(T.bgPanel, isSel ? `${grp.colorHex}14` : null),
                  borderBottom: `1px solid ${T.borderSecondary}15`, borderRight: border,
                  display: "flex", alignItems: "center", cursor: "pointer", paddingLeft: 20,
                }}
                onMouseOver={e => { if (!isSel) e.currentTarget.style.background = `${T.borderPrimary}0d`; }}
                onMouseOut={e => { if (!isSel) e.currentTarget.style.background = isSel ? `${grp.colorHex}14` : T.bgPanel; }}>
                {/* Phase color stripe */}
                <div style={{ width: 3, height: 20, borderRadius: 3, background: grp.colorHex, flexShrink: 0, marginRight: 8 }} />
                <span
                  onClick={e => { e.stopPropagation(); setExpandedSubsSched(pv => { const n = new Set(pv); n.has(sub.id) ? n.delete(sub.id) : n.add(sub.id); return n; }); }}
                  title="Show line items"
                  style={{ fontSize: 10, color: T.textWeak, padding: "0 4px", cursor: "pointer", display: "inline-block", transition: "transform .15s", transform: expandedSubsSched.has(sub.id) ? "rotate(90deg)" : "none", flexShrink: 0 }}>&#9654;</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, color: T.textTertiary, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{sub.name}</div>
                  <div style={{ fontSize: 10, color: T.textWeak, marginTop: 1 }}>{div.code} · {div.name}</div>
                </div>
                {showCost && <div style={{ fontSize: 10, color: T.textTertiary, fontVariantNumeric: "tabular-nums", paddingRight: 10, flexShrink: 0 }}>{fmt(cost)}</div>}
              </div>
              <div style={{ flex: 1, position: "relative", background: isSel ? tintedOver(T.bgPanel, `${grp.colorHex}14`) : T.bgPanel, borderBottom: `1px solid ${T.borderSecondary}15`, overflow: "visible" }}>
                {ticks.map(t => <div key={t.label} style={{ position: "absolute", left: `${t.pct}%`, top: 0, bottom: 0, borderLeft: gridLine }} />)}
                {milestoneLinesInRow}
                {renderSubBar(sub, shade(grp.colorHex), isSel, div.id)}
              </div>
            </div>
            {itemRowsFor(sub, div.id, shade(grp.colorHex))}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  });

  // Empty state: no subcategories with cost yet → nothing to schedule
  const hasScheduleRows = editRowsByDivision.some(Boolean);

  // Fixed to the viewport, so no ancestor's overflow or stacking context can
  // clip it. Flips above the button when it would run off the bottom.
  const phaseMenuOverlay = (() => {
    if (!phaseMenu || !setSubPhase) return null;
    const { divId, sub, x, y } = phaseMenu;
    const ordered = [...phaseGroups].sort((a, b) => a.order - b.order);
    const divPhase = phaseGroups.find(g => g.divIds.includes(divId));
    const pinned = hasPhaseOverride(divId, sub, phaseGroups);
    const W = 210, H = 34 + (ordered.length + 1) * 26;
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    const top = y + H > vh - 8 ? Math.max(8, y - H - 20) : y;
    const row = (on: boolean): React.CSSProperties => ({
      display: "flex", alignItems: "center", gap: 7, width: "100%", textAlign: "left",
      background: on ? T.bgAccentSubtle : "none", border: "none", cursor: "pointer",
      padding: "5px 8px", borderRadius: 3, fontFamily: FONT, fontSize: 11,
      color: on ? T.borderAccent : T.textSecondary,
    });
    return (
      <>
        <div onClick={() => setPhaseMenu(null)} style={{ position: "fixed", inset: 0, zIndex: 9998 }} />
        <div style={{ position: "fixed", top, left: Math.max(8, x - W), width: W, zIndex: 9999, padding: 4,
          background: T.bgModal, border: `1px solid ${T.borderSecondary}`, borderRadius: 6,
          boxShadow: "0 8px 24px rgba(0,0,0,.45)", lineHeight: 1.4 }}>
          <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase", padding: "4px 8px 6px" }}>
            Phase for {sub.name}
          </div>
          <button onClick={() => { setSubPhase(divId, sub.id, undefined); setPhaseMenu(null); }} style={row(!pinned)}>
            <span style={{ width: 9, height: 9, borderRadius: 999, flexShrink: 0,
              background: divPhase?.color ?? "transparent", border: `1px solid ${T.borderSecondary}` }} />
            Follow division{divPhase ? ` (${divPhase.name})` : ""}
          </button>
          {ordered.map(g => (
            <button key={g.id} onClick={() => { setSubPhase(divId, sub.id, g.id); setPhaseMenu(null); }}
              style={row(pinned && sub.phaseId === g.id)}>
              <span style={{ width: 9, height: 9, borderRadius: 999, background: g.color, flexShrink: 0 }} />
              {g.name}
            </button>
          ))}
        </div>
      </>
    );
  })();

  return (
    <div className="oe-schedule-container" style={{ display: "flex", flexDirection: "column", height: "100%", fontFamily: FONT, overflow: "hidden" }}>
      {toolbar}
      {phaseMenuOverlay}
      {!hasScheduleRows ? (
        <EmptyState T={T}
          icon={<Icon.Clock size={32} />}
          headline="Nothing to schedule yet"
          body="The schedule builds itself from your estimate — add line items with costs in the Estimate tab and each subcategory becomes a schedulable bar here."
          ctaLabel="Go to Estimate"
          onCta={()=>setActiveTab?.("estimate")}
        />
      ) : renderGantt(
        <>
          {titleBlock}
          {groupMode === "package" ? editRowsByPackage
            : groupMode === "phase" ? editRowsByPhase
            : editRowsByDivision}
          {printFooter}
        </>
      )}
    </div>
  );
}

// ── Segmented control ────────────────────────────────────────────────
function SegControl({ options, value, onChange, T, border, accent }: {
  options: { v: string; label: string }[];
  value: string; onChange: (v: string) => void;
  T: Theme; border: string; accent: string;
}) {
  return (
    <div style={{ display: "flex", background: T.bgInput, borderRadius: 6, border, overflow: "hidden", flexShrink: 0 }}>
      {options.map(o => (
        <button key={o.v} onClick={() => onChange(o.v)}
          style={{
            // 24px floor: this is a standalone control, and at 20px it was
            // under the touch minimum on a phone. Inline links inside a
            // sentence are a different case and are left at their text size --
            // padding those out would break the line they sit in, and the
            // guidance exempts them for exactly that reason.
            padding: "4px 10px", minHeight: 24, border: "none",
            fontFamily: "'Inter','Avenir Next','Century Gothic',sans-serif",
            fontSize: 10, fontWeight: 700, cursor: "pointer", letterSpacing: ".02em",
            background: o.v === value ? accent : "transparent",
            color:      o.v === value ? "#0a0f1a" : T.textWeak,
            transition: "background .12s, color .12s",
            whiteSpace: "nowrap",
          }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
